import { isComputedProp, isObservableProp, createAtom, action, computed, _allowStateChangesInsideComputed, reaction, runInAction, observable, values, entries, isObservableArray, _getGlobalState, isObservableObject, defineProperty as defineProperty$1, _interceptReads, intercept, observe, ObservableMap, _getAdministration, set, makeObservable, getAtom, when } from 'mobx';

let livelinessChecking = "warn";
/**
 * Defines what MST should do when running into reads / writes to objects that have died.
 * By default it will print a warning.
 * Use the `"error"` option to easy debugging to see where the error was thrown and when the offending read / write took place
 *
 * @param mode `"warn"`, `"error"` or `"ignore"`
 */
function setLivelinessChecking(mode) {
    livelinessChecking = mode;
}
/**
 * Returns the current liveliness checking mode.
 *
 * @returns `"warn"`, `"error"` or `"ignore"`
 */
function getLivelinessChecking() {
    return livelinessChecking;
}
/**
 * @deprecated use setLivelinessChecking instead
 * @hidden
 *
 * Defines what MST should do when running into reads / writes to objects that have died.
 * By default it will print a warning.
 * Use the `"error"` option to easy debugging to see where the error was thrown and when the offending read / write took place
 *
 * @param mode `"warn"`, `"error"` or `"ignore"`
 */
function setLivelynessChecking(mode) {
    setLivelinessChecking(mode);
}

/**
 * @hidden
 */
var Hook;
(function (Hook) {
    Hook["afterCreate"] = "afterCreate";
    Hook["afterAttach"] = "afterAttach";
    Hook["afterCreationFinalization"] = "afterCreationFinalization";
    Hook["beforeDetach"] = "beforeDetach";
    Hook["beforeDestroy"] = "beforeDestroy";
})(Hook || (Hook = {}));

/**
 * Returns the _actual_ type of the given tree node. (Or throws)
 *
 * @param object
 * @returns
 */
function getType(object) {
    assertIsStateTreeNode(object, 1);
    return getStateTreeNode(object).type;
}
/**
 * Returns the _declared_ type of the given sub property of an object, array or map.
 * In the case of arrays and maps the property name is optional and will be ignored.
 *
 * Example:
 * ```ts
 * const Box = types.model({ x: 0, y: 0 })
 * const box = Box.create()
 *
 * console.log(getChildType(box, "x").name) // 'number'
 * ```
 *
 * @param object
 * @param propertyName
 * @returns
 */
function getChildType(object, propertyName) {
    assertIsStateTreeNode(object, 1);
    return getStateTreeNode(object).getChildType(propertyName);
}
/**
 * Registers a function that will be invoked for each mutation that is applied to the provided model instance, or to any of its children.
 * See [patches](https://github.com/mobxjs/mobx-state-tree#patches) for more details. onPatch events are emitted immediately and will not await the end of a transaction.
 * Patches can be used to deeply observe a model tree.
 *
 * @param target the model instance from which to receive patches
 * @param callback the callback that is invoked for each patch. The reversePatch is a patch that would actually undo the emitted patch
 * @returns function to remove the listener
 */
function onPatch(target, callback) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsFunction(callback, 2);
    return getStateTreeNode(target).onPatch(callback);
}
/**
 * Registers a function that is invoked whenever a new snapshot for the given model instance is available.
 * The listener will only be fire at the end of the current MobX (trans)action.
 * See [snapshots](https://github.com/mobxjs/mobx-state-tree#snapshots) for more details.
 *
 * @param target
 * @param callback
 * @returns
 */
function onSnapshot(target, callback) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsFunction(callback, 2);
    return getStateTreeNode(target).onSnapshot(callback);
}
/**
 * Applies a JSON-patch to the given model instance or bails out if the patch couldn't be applied
 * See [patches](https://github.com/mobxjs/mobx-state-tree#patches) for more details.
 *
 * Can apply a single past, or an array of patches.
 *
 * @param target
 * @param patch
 * @returns
 */
function applyPatch(target, patch) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertArg(patch, p => typeof p === "object", "object or array", 2);
    getStateTreeNode(target).applyPatches(asArray(patch));
}
/**
 * Small abstraction around `onPatch` and `applyPatch`, attaches a patch listener to a tree and records all the patches.
 * Returns a recorder object with the following signature:
 *
 * Example:
 * ```ts
 * export interface IPatchRecorder {
 *      // the recorded patches
 *      patches: IJsonPatch[]
 *      // the inverse of the recorded patches
 *      inversePatches: IJsonPatch[]
 *      // true if currently recording
 *      recording: boolean
 *      // stop recording patches
 *      stop(): void
 *      // resume recording patches
 *      resume(): void
 *      // apply all the recorded patches on the given target (the original subject if omitted)
 *      replay(target?: IAnyStateTreeNode): void
 *      // reverse apply the recorded patches on the given target  (the original subject if omitted)
 *      // stops the recorder if not already stopped
 *      undo(): void
 * }
 * ```
 *
 * The optional filter function allows to skip recording certain patches.
 *
 * @param subject
 * @param filter
 * @returns
 */
function recordPatches(subject, filter) {
    // check all arguments
    assertIsStateTreeNode(subject, 1);
    const data = {
        patches: [],
        inversePatches: []
    };
    // we will generate the immutable copy of patches on demand for public consumption
    const publicData = {};
    let disposer;
    const recorder = {
        get recording() {
            return !!disposer;
        },
        get patches() {
            if (!publicData.patches) {
                publicData.patches = data.patches.slice();
            }
            return publicData.patches;
        },
        get reversedInversePatches() {
            if (!publicData.reversedInversePatches) {
                publicData.reversedInversePatches = data.inversePatches
                    .slice()
                    .reverse();
            }
            return publicData.reversedInversePatches;
        },
        get inversePatches() {
            if (!publicData.inversePatches) {
                publicData.inversePatches = data.inversePatches.slice();
            }
            return publicData.inversePatches;
        },
        stop() {
            if (disposer) {
                disposer();
                disposer = undefined;
            }
        },
        resume() {
            if (disposer) {
                return;
            }
            disposer = onPatch(subject, (patch, inversePatch) => {
                // skip patches that are asked to be filtered if there's a filter in place
                if (filter && !filter(patch, inversePatch, getRunningActionContext())) {
                    return;
                }
                data.patches.push(patch);
                data.inversePatches.push(inversePatch);
                // mark immutable public patches as dirty
                publicData.patches = undefined;
                publicData.inversePatches = undefined;
                publicData.reversedInversePatches = undefined;
            });
        },
        replay(target) {
            applyPatch(target || subject, data.patches);
        },
        undo(target) {
            applyPatch(target || subject, data.inversePatches.slice().reverse());
        }
    };
    recorder.resume();
    return recorder;
}
/**
 * The inverse of `unprotect`.
 *
 * @param target
 */
function protect(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    const node = getStateTreeNode(target);
    if (!node.isRoot) {
        throw fail("`protect` can only be invoked on root nodes");
    }
    node.isProtectionEnabled = true;
}
/**
 * By default it is not allowed to directly modify a model. Models can only be modified through actions.
 * However, in some cases you don't care about the advantages (like replayability, traceability, etc) this yields.
 * For example because you are building a PoC or don't have any middleware attached to your tree.
 *
 * In that case you can disable this protection by calling `unprotect` on the root of your tree.
 *
 * Example:
 * ```ts
 * const Todo = types.model({
 *     done: false
 * }).actions(self => ({
 *     toggle() {
 *         self.done = !self.done
 *     }
 * }))
 *
 * const todo = Todo.create()
 * todo.done = true // throws!
 * todo.toggle() // OK
 * unprotect(todo)
 * todo.done = false // OK
 * ```
 */
function unprotect(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    const node = getStateTreeNode(target);
    if (!node.isRoot) {
        throw fail("`unprotect` can only be invoked on root nodes");
    }
    node.isProtectionEnabled = false;
}
/**
 * Returns true if the object is in protected mode, @see protect
 */
function isProtected(target) {
    return getStateTreeNode(target).isProtected;
}
/**
 * Applies a snapshot to a given model instances. Patch and snapshot listeners will be invoked as usual.
 *
 * @param target
 * @param snapshot
 * @returns
 */
function applySnapshot(target, snapshot) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).applySnapshot(snapshot);
}
/**
 * Calculates a snapshot from the given model instance. The snapshot will always reflect the latest state but use
 * structural sharing where possible. Doesn't require MobX transactions to be completed.
 *
 * @param target
 * @param applyPostProcess If true (the default) then postProcessSnapshot gets applied.
 * @returns
 */
function getSnapshot(target, applyPostProcess = true) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    const node = getStateTreeNode(target);
    if (applyPostProcess) {
        return node.snapshot;
    }
    return freeze(node.type.getSnapshot(node, false));
}
/**
 * Given a model instance, returns `true` if the object has a parent, that is, is part of another object, map or array.
 *
 * @param target
 * @param depth How far should we look upward? 1 by default.
 * @returns
 */
function hasParent(target, depth = 1) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsNumber(depth, 2, 0);
    let parent = getStateTreeNode(target).parent;
    while (parent) {
        if (--depth === 0) {
            return true;
        }
        parent = parent.parent;
    }
    return false;
}
/**
 * Returns the immediate parent of this object, or throws.
 *
 * Note that the immediate parent can be either an object, map or array, and
 * doesn't necessarily refer to the parent model.
 *
 * Please note that in child nodes access to the root is only possible
 * once the `afterAttach` hook has fired.
 *
 * @param target
 * @param depth How far should we look upward? 1 by default.
 * @returns
 */
function getParent(target, depth = 1) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsNumber(depth, 2, 0);
    let d = depth;
    let parent = getStateTreeNode(target).parent;
    while (parent) {
        if (--d === 0) {
            return parent.storedValue;
        }
        parent = parent.parent;
    }
    throw fail(`Failed to find the parent of ${getStateTreeNode(target)} at depth ${depth}`);
}
/**
 * Given a model instance, returns `true` if the object has a parent of given type, that is, is part of another object, map or array
 *
 * @param target
 * @param type
 * @returns
 */
function hasParentOfType(target, type) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsType(type, 2);
    let parent = getStateTreeNode(target).parent;
    while (parent) {
        if (type.is(parent.storedValue)) {
            return true;
        }
        parent = parent.parent;
    }
    return false;
}
/**
 * Returns the target's parent of a given type, or throws.
 *
 * @param target
 * @param type
 * @returns
 */
function getParentOfType(target, type) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsType(type, 2);
    let parent = getStateTreeNode(target).parent;
    while (parent) {
        if (type.is(parent.storedValue)) {
            return parent.storedValue;
        }
        parent = parent.parent;
    }
    throw fail(`Failed to find the parent of ${getStateTreeNode(target)} of a given type`);
}
/**
 * Given an object in a model tree, returns the root object of that tree.
 *
 * Please note that in child nodes access to the root is only possible
 * once the `afterAttach` hook has fired.
 *
 * @param target
 * @returns
 */
function getRoot(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).root.storedValue;
}
/**
 * Returns the path of the given object in the model tree
 *
 * @param target
 * @returns
 */
function getPath(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).path;
}
/**
 * Returns the path of the given object as unescaped string array.
 *
 * @param target
 * @returns
 */
function getPathParts(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return splitJsonPath(getStateTreeNode(target).path);
}
/**
 * Returns true if the given object is the root of a model tree.
 *
 * @param target
 * @returns
 */
function isRoot(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).isRoot;
}
/**
 * Resolves a path relatively to a given object.
 * Returns undefined if no value can be found.
 *
 * @param target
 * @param path escaped json path
 * @returns
 */
function resolvePath(target, path) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsString(path, 2);
    const node = resolveNodeByPath(getStateTreeNode(target), path);
    return node ? node.value : undefined;
}
/**
 * Resolves a model instance given a root target, the type and the identifier you are searching for.
 * Returns undefined if no value can be found.
 *
 * @param type
 * @param target
 * @param identifier
 * @returns
 */
function resolveIdentifier(type, target, identifier) {
    // check all arguments
    assertIsType(type, 1);
    assertIsStateTreeNode(target, 2);
    assertIsValidIdentifier(identifier, 3);
    const node = getStateTreeNode(target).root.identifierCache.resolve(type, normalizeIdentifier(identifier));
    return node?.value;
}
/**
 * Returns the identifier of the target node.
 * This is the *string normalized* identifier, which might not match the type of the identifier attribute
 *
 * @param target
 * @returns
 */
function getIdentifier(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).identifier;
}
/**
 * Tests if a reference is valid (pointing to an existing node and optionally if alive) and returns such reference if the check passes,
 * else it returns undefined.
 *
 * @param getter Function to access the reference.
 * @param checkIfAlive true to also make sure the referenced node is alive (default), false to skip this check.
 * @returns
 */
function tryReference(getter, checkIfAlive = true) {
    try {
        const node = getter();
        if (node === undefined || node === null) {
            return undefined;
        }
        else if (isStateTreeNode(node)) {
            if (!checkIfAlive) {
                return node;
            }
            else {
                return isAlive(node) ? node : undefined;
            }
        }
        else {
            throw fail("The reference to be checked is not one of node, null or undefined");
        }
    }
    catch (e) {
        if (e instanceof InvalidReferenceError) {
            return undefined;
        }
        throw e;
    }
}
/**
 * Tests if a reference is valid (pointing to an existing node and optionally if alive) and returns if the check passes or not.
 *
 * @param getter Function to access the reference.
 * @param checkIfAlive true to also make sure the referenced node is alive (default), false to skip this check.
 * @returns
 */
function isValidReference(getter, checkIfAlive = true) {
    try {
        const node = getter();
        if (node === undefined || node === null) {
            return false;
        }
        else if (isStateTreeNode(node)) {
            return checkIfAlive ? isAlive(node) : true;
        }
        else {
            throw fail("The reference to be checked is not one of node, null or undefined");
        }
    }
    catch (e) {
        if (e instanceof InvalidReferenceError) {
            return false;
        }
        throw e;
    }
}
/**
 * Try to resolve a given path relative to a given node.
 *
 * @param target
 * @param path
 * @returns
 */
function tryResolve(target, path) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsString(path, 2);
    const node = resolveNodeByPath(getStateTreeNode(target), path, false);
    if (node === undefined) {
        return undefined;
    }
    try {
        return node.value;
    }
    catch (e) {
        // For what ever reason not resolvable (e.g. totally not existing path, or value that cannot be fetched)
        // see test / issue: 'try resolve doesn't work #686'
        return undefined;
    }
}
/**
 * Given two state tree nodes that are part of the same tree,
 * returns the shortest jsonpath needed to navigate from the one to the other
 *
 * @param base
 * @param target
 * @returns
 */
function getRelativePath(base, target) {
    // check all arguments
    assertIsStateTreeNode(base, 1);
    assertIsStateTreeNode(target, 2);
    return getRelativePathBetweenNodes(getStateTreeNode(base), getStateTreeNode(target));
}
/**
 * Returns a deep copy of the given state tree node as new tree.
 * Shorthand for `snapshot(x) = getType(x).create(getSnapshot(x))`
 *
 * _Tip: clone will create a literal copy, including the same identifiers. To modify identifiers etc. during cloning, don't use clone but take a snapshot of the tree, modify it, and create new instance_
 *
 * @param source
 * @param keepEnvironment indicates whether the clone should inherit the same environment (`true`, the default), or not have an environment (`false`). If an object is passed in as second argument, that will act as the environment for the cloned tree.
 * @returns
 */
function clone(source, keepEnvironment = true) {
    // check all arguments
    assertIsStateTreeNode(source, 1);
    const node = getStateTreeNode(source);
    return node.type.create(node.snapshot, keepEnvironment === true
        ? node.root.environment
        : keepEnvironment === false
            ? undefined
            : keepEnvironment); // it's an object or something else
}
/**
 * Removes a model element from the state tree, and let it live on as a new state tree
 */
function detach(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    getStateTreeNode(target).detach();
    return target;
}
/**
 * Removes a model element from the state tree, and mark it as end-of-life; the element should not be used anymore
 */
function destroy(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    const node = getStateTreeNode(target);
    if (node.isRoot) {
        node.die();
    }
    else {
        node.parent.removeChild(node.subpath);
    }
}
/**
 * Returns true if the given state tree node is not killed yet.
 * This means that the node is still a part of a tree, and that `destroy`
 * has not been called. If a node is not alive anymore, the only thing one can do with it
 * is requesting it's last path and snapshot
 *
 * @param target
 * @returns
 */
function isAlive(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).observableIsAlive;
}
/**
 * Use this utility to register a function that should be called whenever the
 * targeted state tree node is destroyed. This is a useful alternative to managing
 * cleanup methods yourself using the `beforeDestroy` hook.
 *
 * This methods returns the same disposer that was passed as argument.
 *
 * Example:
 * ```ts
 * const Todo = types.model({
 *   title: types.string
 * }).actions(self => ({
 *   afterCreate() {
 *     const autoSaveDisposer = reaction(
 *       () => getSnapshot(self),
 *       snapshot => sendSnapshotToServerSomehow(snapshot)
 *     )
 *     // stop sending updates to server if this
 *     // instance is destroyed
 *     addDisposer(self, autoSaveDisposer)
 *   }
 * }))
 * ```
 *
 * @param target
 * @param disposer
 * @returns The same disposer that was passed as argument
 */
function addDisposer(target, disposer) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsFunction(disposer, 2);
    const node = getStateTreeNode(target);
    node.addDisposer(disposer);
    return disposer;
}
/**
 * Returns the environment of the current state tree. For more info on environments,
 * see [Dependency injection](https://github.com/mobxjs/mobx-state-tree#dependency-injection)
 *
 * Please note that in child nodes access to the root is only possible
 * once the `afterAttach` hook has fired
 *
 * Returns an empty environment if the tree wasn't initialized with an environment
 *
 * @param target
 * @returns
 */
function getEnv(target) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    const node = getStateTreeNode(target);
    const env = node.root.environment;
    if (!env) {
        return EMPTY_OBJECT;
    }
    return env;
}
/**
 * Performs a depth first walk through a tree.
 */
function walk(target, processor) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertIsFunction(processor, 2);
    const node = getStateTreeNode(target);
    // tslint:disable-next-line:no_unused-variable
    node.getChildren().forEach(child => {
        if (isStateTreeNode(child.storedValue)) {
            walk(child.storedValue, processor);
        }
    });
    processor(node.storedValue);
}
/**
 * Returns a reflection of the model type properties and name for either a model type or model node.
 *
 * @param typeOrNode
 * @returns
 */
function getPropertyMembers(typeOrNode) {
    let type;
    if (isStateTreeNode(typeOrNode)) {
        type = getType(typeOrNode);
    }
    else {
        type = typeOrNode;
    }
    assertArg(type, t => isModelType(t), "model type or model instance", 1);
    return {
        name: type.name,
        properties: { ...type.properties }
    };
}
/**
 * Returns a reflection of the model node, including name, properties, views, volatile state,
 * and actions. `flowActions` is also provided as a separate array of names for any action that
 * came from a flow generator as well.
 *
 * In the case where a model has two actions: `doSomething` and `doSomethingWithFlow`, where
 * `doSomethingWithFlow` is a flow generator, the `actions` array will contain both actions,
 * i.e. ["doSomething", "doSomethingWithFlow"], and the `flowActions` array will contain only
 * the flow action, i.e. ["doSomethingWithFlow"].
 *
 * @param target
 * @returns
 */
function getMembers(target) {
    const type = getStateTreeNode(target).type;
    const reflected = {
        ...getPropertyMembers(type),
        actions: [],
        volatile: [],
        views: [],
        flowActions: []
    };
    const props = Object.getOwnPropertyNames(target);
    props.forEach(key => {
        if (key in reflected.properties) {
            return;
        }
        const descriptor = Object.getOwnPropertyDescriptor(target, key);
        if (descriptor.get) {
            if (isComputedProp(target, key)) {
                reflected.views.push(key);
            }
            else {
                reflected.volatile.push(key);
            }
            return;
        }
        if (descriptor.value._isFlowAction === true) {
            reflected.flowActions.push(key);
        }
        if (descriptor.value._isMSTAction === true) {
            reflected.actions.push(key);
        }
        else if (isObservableProp(target, key)) {
            reflected.volatile.push(key);
        }
        else {
            reflected.views.push(key);
        }
    });
    return reflected;
}
/**
 * Casts a node snapshot or instance type to an instance type so it can be assigned to a type instance.
 * Note that this is just a cast for the type system, this is, it won't actually convert a snapshot to an instance,
 * but just fool typescript into thinking so.
 * Either way, casting when outside an assignation operation won't compile.
 *
 * Example:
 * ```ts
 * const ModelA = types.model({
 *   n: types.number
 * }).actions(self => ({
 *   setN(aNumber: number) {
 *     self.n = aNumber
 *   }
 * }))
 *
 * const ModelB = types.model({
 *   innerModel: ModelA
 * }).actions(self => ({
 *   someAction() {
 *     // this will allow the compiler to assign a snapshot to the property
 *     self.innerModel = cast({ a: 5 })
 *   }
 * }))
 * ```
 *
 * @param snapshotOrInstance Snapshot or instance
 * @returns The same object cast as an instance
 */
function cast(snapshotOrInstance) {
    return snapshotOrInstance;
}
/**
 * Casts a node instance type to a snapshot type so it can be assigned to a type snapshot (e.g. to be used inside a create call).
 * Note that this is just a cast for the type system, this is, it won't actually convert an instance to a snapshot,
 * but just fool typescript into thinking so.
 *
 * Example:
 * ```ts
 * const ModelA = types.model({
 *   n: types.number
 * }).actions(self => ({
 *   setN(aNumber: number) {
 *     self.n = aNumber
 *   }
 * }))
 *
 * const ModelB = types.model({
 *   innerModel: ModelA
 * })
 *
 * const a = ModelA.create({ n: 5 });
 * // this will allow the compiler to use a model as if it were a snapshot
 * const b = ModelB.create({ innerModel: castToSnapshot(a)})
 * ```
 *
 * @param snapshotOrInstance Snapshot or instance
 * @returns The same object cast as an input (creation) snapshot
 */
function castToSnapshot(snapshotOrInstance) {
    return snapshotOrInstance;
}
/**
 * Casts a node instance type to a reference snapshot type so it can be assigned to a reference snapshot (e.g. to be used inside a create call).
 * Note that this is just a cast for the type system, this is, it won't actually convert an instance to a reference snapshot,
 * but just fool typescript into thinking so.
 *
 * Example:
 * ```ts
 * const ModelA = types.model({
 *   id: types.identifier,
 *   n: types.number
 * }).actions(self => ({
 *   setN(aNumber: number) {
 *     self.n = aNumber
 *   }
 * }))
 *
 * const ModelB = types.model({
 *   refA: types.reference(ModelA)
 * })
 *
 * const a = ModelA.create({ id: 'someId', n: 5 });
 * // this will allow the compiler to use a model as if it were a reference snapshot
 * const b = ModelB.create({ refA: castToReferenceSnapshot(a)})
 * ```
 *
 * @param instance Instance
 * @returns The same object cast as a reference snapshot (string or number)
 */
function castToReferenceSnapshot(instance) {
    return instance;
}
/**
 * Returns the unique node id (not to be confused with the instance identifier) for a
 * given instance.
 * This id is a number that is unique for each instance.
 *
 * @export
 * @param target
 * @returns
 */
function getNodeId(target) {
    assertIsStateTreeNode(target, 1);
    return getStateTreeNode(target).nodeId;
}

/**
 * @internal
 * @hidden
 */
class BaseNode {
    type;
    environment;
    _escapedSubpath;
    _subpath;
    get subpath() {
        return this._subpath;
    }
    _subpathUponDeath;
    get subpathUponDeath() {
        return this._subpathUponDeath;
    }
    _pathUponDeath;
    get pathUponDeath() {
        return this._pathUponDeath;
    }
    storedValue; // usually the same type as the value, but not always (such as with references)
    get value() {
        return this.type.getValue(this);
    }
    aliveAtom;
    _state = NodeLifeCycle.INITIALIZING;
    get state() {
        return this._state;
    }
    set state(val) {
        const wasAlive = this.isAlive;
        this._state = val;
        const isAlive = this.isAlive;
        if (this.aliveAtom && wasAlive !== isAlive) {
            this.aliveAtom.reportChanged();
        }
    }
    _hookSubscribers;
    fireInternalHook(name) {
        if (this._hookSubscribers) {
            this._hookSubscribers.emit(name, this, name);
        }
    }
    registerHook(hook, hookHandler) {
        if (!this._hookSubscribers) {
            this._hookSubscribers = new EventHandlers();
        }
        return this._hookSubscribers.register(hook, hookHandler);
    }
    _parent;
    get parent() {
        return this._parent;
    }
    constructor(type, parent, subpath, environment) {
        this.type = type;
        this.environment = environment;
        this.environment = environment;
        this.baseSetParent(parent, subpath);
    }
    getReconciliationType() {
        return this.type;
    }
    pathAtom;
    baseSetParent(parent, subpath) {
        this._parent = parent;
        this._subpath = subpath;
        this._escapedSubpath = undefined; // regenerate when needed
        if (this.pathAtom) {
            this.pathAtom.reportChanged();
        }
    }
    /*
     * Returns (escaped) path representation as string
     */
    get path() {
        return this.getEscapedPath(true);
    }
    getEscapedPath(reportObserved) {
        if (reportObserved) {
            if (!this.pathAtom) {
                this.pathAtom = createAtom(`path`);
            }
            this.pathAtom.reportObserved();
        }
        if (!this.parent) {
            return "";
        }
        // regenerate escaped subpath if needed
        if (this._escapedSubpath === undefined) {
            this._escapedSubpath = !this._subpath ? "" : escapeJsonPath(this._subpath);
        }
        return (this.parent.getEscapedPath(reportObserved) + "/" + this._escapedSubpath);
    }
    get isRoot() {
        return this.parent === null;
    }
    get isAlive() {
        return this.state !== NodeLifeCycle.DEAD;
    }
    get isDetaching() {
        return this.state === NodeLifeCycle.DETACHING;
    }
    get observableIsAlive() {
        if (!this.aliveAtom) {
            this.aliveAtom = createAtom(`alive`);
        }
        this.aliveAtom.reportObserved();
        return this.isAlive;
    }
    baseFinalizeCreation(whenFinalized) {
        if (devMode()) {
            if (!this.isAlive) {
                // istanbul ignore next
                throw fail("assertion failed: cannot finalize the creation of a node that is already dead");
            }
        }
        // goal: afterCreate hooks runs depth-first. After attach runs parent first, so on afterAttach the parent has completed already
        if (this.state === NodeLifeCycle.CREATED) {
            if (this.parent) {
                if (this.parent.state !== NodeLifeCycle.FINALIZED) {
                    // parent not ready yet, postpone
                    return;
                }
                this.fireHook(Hook.afterAttach);
            }
            this.state = NodeLifeCycle.FINALIZED;
            if (whenFinalized) {
                whenFinalized();
            }
        }
    }
    baseFinalizeDeath() {
        if (this._hookSubscribers) {
            this._hookSubscribers.clearAll();
        }
        this._subpathUponDeath = this._subpath;
        this._pathUponDeath = this.getEscapedPath(false);
        this.baseSetParent(null, "");
        this.state = NodeLifeCycle.DEAD;
    }
    baseAboutToDie() {
        this.fireHook(Hook.beforeDestroy);
    }
}

/**
 * @internal
 * @hidden
 */
class ScalarNode extends BaseNode {
    constructor(simpleType, parent, subpath, environment, initialSnapshot) {
        super(simpleType, parent, subpath, environment);
        try {
            this.storedValue = simpleType.createNewInstance(initialSnapshot);
        }
        catch (e) {
            // short-cut to die the instance, to avoid the snapshot computed starting to throw...
            this.state = NodeLifeCycle.DEAD;
            throw e;
        }
        this.state = NodeLifeCycle.CREATED;
        // for scalar nodes there's no point in firing this event since it would fire on the constructor, before
        // anybody can actually register for/listen to it
        // this.fireHook(Hook.AfterCreate)
        this.finalizeCreation();
    }
    get root() {
        // future optimization: store root ref in the node and maintain it
        if (!this.parent) {
            throw fail(`This scalar node is not part of a tree`);
        }
        return this.parent.root;
    }
    setParent(newParent, subpath) {
        const parentChanged = this.parent !== newParent;
        const subpathChanged = this.subpath !== subpath;
        if (!parentChanged && !subpathChanged) {
            return;
        }
        if (devMode()) {
            if (!subpath) {
                // istanbul ignore next
                throw fail("assertion failed: subpath expected");
            }
            if (!newParent) {
                // istanbul ignore next
                throw fail("assertion failed: parent expected");
            }
            if (parentChanged) {
                // istanbul ignore next
                throw fail("assertion failed: scalar nodes cannot change their parent");
            }
        }
        this.environment = undefined; // use parent's
        this.baseSetParent(this.parent, subpath);
    }
    get snapshot() {
        return freeze(this.getSnapshot());
    }
    getSnapshot() {
        return this.type.getSnapshot(this);
    }
    toString() {
        const path = (this.isAlive ? this.path : this.pathUponDeath) || "<root>";
        return `${this.type.name}@${path}${this.isAlive ? "" : " [dead]"}`;
    }
    die() {
        if (!this.isAlive || this.state === NodeLifeCycle.DETACHING) {
            return;
        }
        this.aboutToDie();
        this.finalizeDeath();
    }
    finalizeCreation() {
        this.baseFinalizeCreation();
    }
    aboutToDie() {
        this.baseAboutToDie();
    }
    finalizeDeath() {
        this.baseFinalizeDeath();
    }
    fireHook(name) {
        this.fireInternalHook(name);
    }
}
ScalarNode.prototype.die = action(ScalarNode.prototype.die);

// noinspection ES6UnusedImports
let nextNodeId = 1;
const snapshotReactionOptions = {
    onError(e) {
        throw e;
    }
};
/**
 * @internal
 * @hidden
 */
class ObjectNode extends BaseNode {
    nodeId = ++nextNodeId;
    identifierAttribute;
    identifier; // Identifier is always normalized to string, even if the identifier property isn't
    unnormalizedIdentifier;
    identifierCache;
    isProtectionEnabled = true;
    middlewares;
    hasSnapshotPostProcessor = false;
    _applyPatches;
    applyPatches(patches) {
        this.createObservableInstanceIfNeeded();
        this._applyPatches(patches);
    }
    _applySnapshot;
    applySnapshot(snapshot) {
        this.createObservableInstanceIfNeeded();
        this._applySnapshot(snapshot);
    }
    _autoUnbox = true; // unboxing is disabled when reading child nodes
    _isRunningAction = false; // only relevant for root
    _hasSnapshotReaction = false;
    _observableInstanceState = 0 /* ObservableInstanceLifecycle.UNINITIALIZED */;
    _childNodes;
    _initialSnapshot;
    _cachedInitialSnapshot;
    _cachedInitialSnapshotCreated = false;
    _snapshotComputed;
    constructor(complexType, parent, subpath, environment, initialValue) {
        super(complexType, parent, subpath, environment);
        this._snapshotComputed = computed(() => freeze(this.getSnapshot()));
        this.unbox = this.unbox.bind(this);
        this._initialSnapshot = freeze(initialValue);
        this.identifierAttribute = complexType.identifierAttribute;
        if (!parent) {
            this.identifierCache = new IdentifierCache();
        }
        this._childNodes = complexType.initializeChildNodes(this, this._initialSnapshot);
        // identifier can not be changed during lifecycle of a node
        // so we safely can read it from initial snapshot
        this.identifier = null;
        this.unnormalizedIdentifier = null;
        if (this.identifierAttribute && this._initialSnapshot) {
            let id = this._initialSnapshot[this.identifierAttribute];
            if (id === undefined) {
                // try with the actual node if not (for optional identifiers)
                const childNode = this._childNodes[this.identifierAttribute];
                if (childNode) {
                    id = childNode.value;
                }
            }
            if (typeof id !== "string" && typeof id !== "number") {
                throw fail(`Instance identifier '${this.identifierAttribute}' for type '${this.type.name}' must be a string or a number`);
            }
            // normalize internal identifier to string
            this.identifier = normalizeIdentifier(id);
            this.unnormalizedIdentifier = id;
        }
        if (!parent) {
            this.identifierCache.addNodeToCache(this);
        }
        else {
            parent.root.identifierCache.addNodeToCache(this);
        }
    }
    createObservableInstanceIfNeeded(fireHooks = true) {
        if (this._observableInstanceState ===
            0 /* ObservableInstanceLifecycle.UNINITIALIZED */) {
            this.createObservableInstance(fireHooks);
        }
    }
    createObservableInstance(fireHooks = true) {
        if (devMode()) {
            if (this.state !== NodeLifeCycle.INITIALIZING) {
                // istanbul ignore next
                throw fail("assertion failed: the creation of the observable instance must be done on the initializing phase");
            }
        }
        this._observableInstanceState = 1 /* ObservableInstanceLifecycle.CREATING */;
        // make sure the parent chain is created as well
        // array with parent chain from parent to child
        const parentChain = [];
        let parent = this.parent;
        // for performance reasons we never go back further than the most direct
        // uninitialized parent
        // this is done to avoid traversing the whole tree to the root when using
        // the same reference again
        while (parent &&
            parent._observableInstanceState ===
                0 /* ObservableInstanceLifecycle.UNINITIALIZED */) {
            parentChain.unshift(parent);
            parent = parent.parent;
        }
        // initialize the uninitialized parent chain from parent to child
        for (const p of parentChain) {
            // delay firing hooks until after all parents have been created
            p.createObservableInstanceIfNeeded(false);
        }
        const type = this.type;
        try {
            // @ts-expect-error
            this.storedValue = type.createNewInstance(this._childNodes);
            this.preboot();
            this._isRunningAction = true;
            type.finalizeNewInstance(this, this.storedValue);
        }
        catch (e) {
            // short-cut to die the instance, to avoid the snapshot computed starting to throw...
            this.state = NodeLifeCycle.DEAD;
            throw e;
        }
        finally {
            this._isRunningAction = false;
        }
        this._observableInstanceState = 2 /* ObservableInstanceLifecycle.CREATED */;
        this._snapshotComputed.trackAndCompute();
        if (this.isRoot) {
            this._addSnapshotReaction();
        }
        this._childNodes = EMPTY_OBJECT;
        this.state = NodeLifeCycle.CREATED;
        if (fireHooks) {
            this.fireHook(Hook.afterCreate);
            // Note that the parent might not be finalized at this point
            // so afterAttach won't be called until later in that case
            this.finalizeCreation();
            // fire the hooks of the parents that we created
            for (const p of parentChain.reverse()) {
                p.fireHook(Hook.afterCreate);
                // This will call afterAttach on the child if necessary
                p.finalizeCreation();
            }
        }
    }
    get root() {
        const parent = this.parent;
        return parent ? parent.root : this;
    }
    clearParent() {
        if (!this.parent) {
            return;
        }
        // detach if attached
        this.fireHook(Hook.beforeDetach);
        const previousState = this.state;
        this.state = NodeLifeCycle.DETACHING;
        const root = this.root;
        const newEnv = root.environment;
        const newIdCache = root.identifierCache.splitCache(this);
        try {
            this.parent.removeChild(this.subpath);
            this.baseSetParent(null, "");
            this.environment = newEnv;
            this.identifierCache = newIdCache;
        }
        finally {
            this.state = previousState;
        }
    }
    setParent(newParent, subpath) {
        const parentChanged = newParent !== this.parent;
        const subpathChanged = subpath !== this.subpath;
        if (!parentChanged && !subpathChanged) {
            return;
        }
        if (devMode()) {
            if (!subpath) {
                // istanbul ignore next
                throw fail("assertion failed: subpath expected");
            }
            if (!newParent) {
                // istanbul ignore next
                throw fail("assertion failed: new parent expected");
            }
            if (this.parent && parentChanged) {
                throw fail(`A node cannot exists twice in the state tree. Failed to add ${this} to path '${newParent.path}/${subpath}'.`);
            }
            if (!this.parent && newParent.root === this) {
                throw fail(`A state tree is not allowed to contain itself. Cannot assign ${this} to path '${newParent.path}/${subpath}'`);
            }
            if (!this.parent &&
                !!this.environment &&
                this.environment !== newParent.root.environment) {
                throw fail(`A state tree cannot be made part of another state tree as long as their environments are different.`);
            }
        }
        if (parentChanged) {
            // attach to new parent
            this.environment = undefined; // will use root's
            newParent.root.identifierCache.mergeCache(this);
            this.baseSetParent(newParent, subpath);
            this.fireHook(Hook.afterAttach);
        }
        else if (subpathChanged) {
            // moving to a new subpath on the same parent
            this.baseSetParent(this.parent, subpath);
        }
    }
    fireHook(name) {
        this.fireInternalHook(name);
        const fn = this.storedValue &&
            typeof this.storedValue === "object" &&
            this.storedValue[name];
        if (typeof fn === "function") {
            // we check for it to allow old mobx peer dependencies that don't have the method to work (even when still bugged)
            if (_allowStateChangesInsideComputed) {
                _allowStateChangesInsideComputed(() => {
                    fn.apply(this.storedValue);
                });
            }
            else {
                fn.apply(this.storedValue);
            }
        }
    }
    _snapshotUponDeath;
    // advantage of using computed for a snapshot is that nicely respects transactions etc.
    get snapshot() {
        if (this.hasSnapshotPostProcessor) {
            this.createObservableInstanceIfNeeded();
        }
        return this._snapshotComputed.get();
    }
    // NOTE: we use this method to get snapshot without creating @computed overhead
    getSnapshot() {
        if (!this.isAlive) {
            return this._snapshotUponDeath;
        }
        return this._observableInstanceState === 2 /* ObservableInstanceLifecycle.CREATED */
            ? this._getActualSnapshot()
            : this._getCachedInitialSnapshot();
    }
    _getActualSnapshot() {
        return this.type.getSnapshot(this);
    }
    _getCachedInitialSnapshot() {
        if (!this._cachedInitialSnapshotCreated) {
            const type = this.type;
            const childNodes = this._childNodes;
            const snapshot = this._initialSnapshot;
            this._cachedInitialSnapshot = type.processInitialSnapshot(childNodes, snapshot);
            this._cachedInitialSnapshotCreated = true;
        }
        return this._cachedInitialSnapshot;
    }
    isRunningAction() {
        if (this._isRunningAction) {
            return true;
        }
        if (this.isRoot) {
            return false;
        }
        return this.parent.isRunningAction();
    }
    assertAlive(context) {
        const livelinessChecking = getLivelinessChecking();
        if (!this.isAlive && livelinessChecking !== "ignore") {
            const error = this._getAssertAliveError(context);
            switch (livelinessChecking) {
                case "error":
                    throw fail(error);
                case "warn":
                    warnError(error);
            }
        }
    }
    _getAssertAliveError(context) {
        const escapedPath = this.getEscapedPath(false) || this.pathUponDeath || "";
        const subpath = (context.subpath && escapeJsonPath(context.subpath)) || "";
        let actionContext = context.actionContext || getCurrentActionContext();
        // try to use a real action context if possible since it includes the action name
        if (actionContext &&
            actionContext.type !== "action" &&
            actionContext.parentActionEvent) {
            actionContext = actionContext.parentActionEvent;
        }
        let actionFullPath = "";
        if (actionContext && actionContext.name != null) {
            // try to use the context, and if it not available use the node one
            const actionPath = (actionContext &&
                actionContext.context &&
                getPath(actionContext.context)) ||
                escapedPath;
            actionFullPath = `${actionPath}.${actionContext.name}()`;
        }
        return `You are trying to read or write to an object that is no longer part of a state tree. (Object type: '${this.type.name}', Path upon death: '${escapedPath}', Subpath: '${subpath}', Action: '${actionFullPath}'). Either detach nodes first, or don't use objects after removing / replacing them in the tree.`;
    }
    getChildNode(subpath) {
        this.assertAlive({
            subpath
        });
        this._autoUnbox = false;
        try {
            return this._observableInstanceState ===
                2 /* ObservableInstanceLifecycle.CREATED */
                ? this.type.getChildNode(this, subpath)
                : this._childNodes[subpath];
        }
        finally {
            this._autoUnbox = true;
        }
    }
    getChildren() {
        this.assertAlive(EMPTY_OBJECT);
        this._autoUnbox = false;
        try {
            return this._observableInstanceState ===
                2 /* ObservableInstanceLifecycle.CREATED */
                ? this.type.getChildren(this)
                : convertChildNodesToArray(this._childNodes);
        }
        finally {
            this._autoUnbox = true;
        }
    }
    getChildType(propertyName) {
        return this.type.getChildType(propertyName);
    }
    get isProtected() {
        return this.root.isProtectionEnabled;
    }
    assertWritable(context) {
        this.assertAlive(context);
        if (!this.isRunningAction() && this.isProtected) {
            throw fail(`Cannot modify '${this}', the object is protected and can only be modified by using an action.`);
        }
    }
    removeChild(subpath) {
        this.type.removeChild(this, subpath);
    }
    // bound on the constructor
    unbox(childNode) {
        if (!childNode) {
            return childNode;
        }
        this.assertAlive({
            subpath: childNode.subpath || childNode.subpathUponDeath
        });
        return this._autoUnbox ? childNode.value : childNode;
    }
    toString() {
        const path = (this.isAlive ? this.path : this.pathUponDeath) || "<root>";
        const identifier = this.identifier ? `(id: ${this.identifier})` : "";
        return `${this.type.name}@${path}${identifier}${this.isAlive ? "" : " [dead]"}`;
    }
    finalizeCreation() {
        this.baseFinalizeCreation(() => {
            for (const child of this.getChildren()) {
                child.finalizeCreation();
            }
            this.fireInternalHook(Hook.afterCreationFinalization);
        });
    }
    detach() {
        if (!this.isAlive) {
            throw fail(`Error while detaching, node is not alive.`);
        }
        this.clearParent();
    }
    preboot() {
        const self = this;
        this._applyPatches = createActionInvoker(this.storedValue, "@APPLY_PATCHES", (patches) => {
            patches.forEach(patch => {
                if (!patch.path) {
                    self.type.applySnapshot(self, patch.value);
                    return;
                }
                const parts = splitJsonPath(patch.path);
                const node = resolveNodeByPathParts(self, parts.slice(0, -1));
                node.applyPatchLocally(parts[parts.length - 1], patch);
            });
        });
        this._applySnapshot = createActionInvoker(this.storedValue, "@APPLY_SNAPSHOT", (snapshot) => {
            // if the snapshot is the same as the current one, avoid performing a reconcile
            if (snapshot === self.snapshot) {
                return;
            }
            // else, apply it by calling the type logic
            return self.type.applySnapshot(self, snapshot);
        });
        addHiddenFinalProp(this.storedValue, "$treenode", this);
        addHiddenFinalProp(this.storedValue, "toJSON", toJSON);
    }
    die() {
        if (!this.isAlive || this.state === NodeLifeCycle.DETACHING) {
            return;
        }
        this.aboutToDie();
        this.finalizeDeath();
    }
    aboutToDie() {
        if (this._observableInstanceState ===
            0 /* ObservableInstanceLifecycle.UNINITIALIZED */) {
            return;
        }
        this.getChildren().forEach(node => {
            node.aboutToDie();
        });
        // beforeDestroy should run before the disposers since else we could end up in a situation where
        // a disposer added with addDisposer at this stage (beforeDestroy) is actually never released
        this.baseAboutToDie();
        this._internalEventsEmit("dispose" /* InternalEvents.Dispose */);
        this._internalEventsClear("dispose" /* InternalEvents.Dispose */);
    }
    finalizeDeath() {
        // invariant: not called directly but from "die"
        this.getChildren().forEach(node => {
            node.finalizeDeath();
        });
        this.root.identifierCache.notifyDied(this);
        // "kill" the computed prop and just store the last snapshot
        const snapshot = this.snapshot;
        this._snapshotUponDeath = snapshot;
        this._internalEventsClearAll();
        this.baseFinalizeDeath();
    }
    onSnapshot(onChange) {
        this._addSnapshotReaction();
        return this._internalEventsRegister("snapshot" /* InternalEvents.Snapshot */, onChange);
    }
    emitSnapshot(snapshot) {
        this._internalEventsEmit("snapshot" /* InternalEvents.Snapshot */, snapshot);
    }
    onPatch(handler) {
        return this._internalEventsRegister("patch" /* InternalEvents.Patch */, handler);
    }
    emitPatch(basePatch, source) {
        if (this._internalEventsHasSubscribers("patch" /* InternalEvents.Patch */)) {
            const localizedPatch = extend({}, basePatch, {
                path: source.path.substr(this.path.length) + "/" + basePatch.path // calculate the relative path of the patch
            });
            const [patch, reversePatch] = splitPatch(localizedPatch);
            this._internalEventsEmit("patch" /* InternalEvents.Patch */, patch, reversePatch);
        }
        if (this.parent) {
            this.parent.emitPatch(basePatch, source);
        }
    }
    hasDisposer(disposer) {
        return this._internalEventsHas("dispose" /* InternalEvents.Dispose */, disposer);
    }
    addDisposer(disposer) {
        if (!this.hasDisposer(disposer)) {
            this._internalEventsRegister("dispose" /* InternalEvents.Dispose */, disposer, true);
            return;
        }
        throw fail("cannot add a disposer when it is already registered for execution");
    }
    removeDisposer(disposer) {
        if (!this._internalEventsHas("dispose" /* InternalEvents.Dispose */, disposer)) {
            throw fail("cannot remove a disposer which was never registered for execution");
        }
        this._internalEventsUnregister("dispose" /* InternalEvents.Dispose */, disposer);
    }
    removeMiddleware(middleware) {
        if (this.middlewares) {
            const index = this.middlewares.indexOf(middleware);
            if (index >= 0) {
                this.middlewares.splice(index, 1);
            }
        }
    }
    addMiddleWare(handler, includeHooks = true) {
        const middleware = { handler, includeHooks };
        if (!this.middlewares) {
            this.middlewares = [middleware];
        }
        else {
            this.middlewares.push(middleware);
        }
        return () => {
            this.removeMiddleware(middleware);
        };
    }
    applyPatchLocally(subpath, patch) {
        this.assertWritable({
            subpath
        });
        this.createObservableInstanceIfNeeded();
        this.type.applyPatchLocally(this, subpath, patch);
    }
    _addSnapshotReaction() {
        if (!this._hasSnapshotReaction) {
            const snapshotDisposer = reaction(() => this.snapshot, snapshot => this.emitSnapshot(snapshot), snapshotReactionOptions);
            this.addDisposer(snapshotDisposer);
            this._hasSnapshotReaction = true;
        }
    }
    // #region internal event handling
    _internalEvents;
    // we proxy the methods to avoid creating an EventHandlers instance when it is not needed
    _internalEventsHasSubscribers(event) {
        return !!this._internalEvents && this._internalEvents.hasSubscribers(event);
    }
    _internalEventsRegister(event, eventHandler, atTheBeginning = false) {
        if (!this._internalEvents) {
            this._internalEvents = new EventHandlers();
        }
        return this._internalEvents.register(event, eventHandler, atTheBeginning);
    }
    _internalEventsHas(event, eventHandler) {
        return (!!this._internalEvents && this._internalEvents.has(event, eventHandler));
    }
    _internalEventsUnregister(event, eventHandler) {
        if (this._internalEvents) {
            this._internalEvents.unregister(event, eventHandler);
        }
    }
    _internalEventsEmit(event, ...args) {
        if (this._internalEvents) {
            this._internalEvents.emit(event, ...args);
        }
    }
    _internalEventsClear(event) {
        if (this._internalEvents) {
            this._internalEvents.clear(event);
        }
    }
    _internalEventsClearAll() {
        if (this._internalEvents) {
            this._internalEvents.clearAll();
        }
    }
}
ObjectNode.prototype.createObservableInstance = action(ObjectNode.prototype.createObservableInstance);
ObjectNode.prototype.detach = action(ObjectNode.prototype.detach);
ObjectNode.prototype.die = action(ObjectNode.prototype.die);

// Cache for validation results to avoid re-validating the same object against the same type
// Uses WeakMap so cached objects can be garbage collected
const validationCache = new WeakMap();
/**
 * @internal
 * @hidden
 */
var TypeFlags;
(function (TypeFlags) {
    TypeFlags[TypeFlags["String"] = 1] = "String";
    TypeFlags[TypeFlags["Number"] = 2] = "Number";
    TypeFlags[TypeFlags["Boolean"] = 4] = "Boolean";
    TypeFlags[TypeFlags["Date"] = 8] = "Date";
    TypeFlags[TypeFlags["Literal"] = 16] = "Literal";
    TypeFlags[TypeFlags["Array"] = 32] = "Array";
    TypeFlags[TypeFlags["Map"] = 64] = "Map";
    TypeFlags[TypeFlags["Object"] = 128] = "Object";
    TypeFlags[TypeFlags["Frozen"] = 256] = "Frozen";
    TypeFlags[TypeFlags["Optional"] = 512] = "Optional";
    TypeFlags[TypeFlags["Reference"] = 1024] = "Reference";
    TypeFlags[TypeFlags["Identifier"] = 2048] = "Identifier";
    TypeFlags[TypeFlags["Late"] = 4096] = "Late";
    TypeFlags[TypeFlags["Refinement"] = 8192] = "Refinement";
    TypeFlags[TypeFlags["Union"] = 16384] = "Union";
    TypeFlags[TypeFlags["Null"] = 32768] = "Null";
    TypeFlags[TypeFlags["Undefined"] = 65536] = "Undefined";
    TypeFlags[TypeFlags["Integer"] = 131072] = "Integer";
    TypeFlags[TypeFlags["Custom"] = 262144] = "Custom";
    TypeFlags[TypeFlags["SnapshotProcessor"] = 524288] = "SnapshotProcessor";
    TypeFlags[TypeFlags["Lazy"] = 1048576] = "Lazy";
    TypeFlags[TypeFlags["Finite"] = 2097152] = "Finite";
    TypeFlags[TypeFlags["Float"] = 4194304] = "Float";
})(TypeFlags || (TypeFlags = {}));
/**
 * @internal
 * @hidden
 */
const cannotDetermineSubtype = "cannotDetermine";
/** @hidden */
const $type = Symbol("$type");
/**
 * A base type produces a MST node (Node in the state tree)
 *
 * @internal
 * @hidden
 */
class BaseType {
    [$type];
    // these are just to make inner types avaialable to inherited classes
    C;
    S;
    T;
    N;
    isType = true;
    name;
    constructor(name) {
        this.name = name;
    }
    create(snapshot, environment) {
        typecheckInternal(this, snapshot);
        return this.instantiate(null, "", environment, snapshot).value;
    }
    getSnapshot(node, applyPostProcess) {
        // istanbul ignore next
        throw fail("unimplemented method");
    }
    isAssignableFrom(type) {
        return type === this;
    }
    validate(value, context) {
        const node = getStateTreeNodeSafe(value);
        if (node) {
            const valueType = getType(value);
            return this.isAssignableFrom(valueType)
                ? typeCheckSuccess()
                : typeCheckFailure(context, value);
            // it is tempting to compare snapshots, but in that case we should always clone on assignments...
        }
        // check cache for object values (only at root level to avoid context mismatches)
        if (typeof value === "object" && value !== null && context.length === 1) {
            const typeCache = validationCache.get(value);
            if (typeCache) {
                const cached = typeCache.get(this);
                if (cached !== undefined) {
                    return cached;
                }
            }
        }
        const result = this.isValidSnapshot(value, context);
        // cache result for object values (only at root level)
        if (typeof value === "object" && value !== null && context.length === 1) {
            let typeCache = validationCache.get(value);
            if (!typeCache) {
                typeCache = new WeakMap();
                validationCache.set(value, typeCache);
            }
            typeCache.set(this, result);
        }
        return result;
    }
    is(thing) {
        return this.validate(thing, [{ path: "", type: this }]).length === 0;
    }
    get Type() {
        // istanbul ignore next
        throw fail("Factory.Type should not be actually called. It is just a Type signature that can be used at compile time with Typescript, by using `typeof type.Type`");
    }
    get TypeWithoutSTN() {
        // istanbul ignore next
        throw fail("Factory.TypeWithoutSTN should not be actually called. It is just a Type signature that can be used at compile time with Typescript, by using `typeof type.TypeWithoutSTN`");
    }
    get SnapshotType() {
        // istanbul ignore next
        throw fail("Factory.SnapshotType should not be actually called. It is just a Type signature that can be used at compile time with Typescript, by using `typeof type.SnapshotType`");
    }
    get CreationType() {
        // istanbul ignore next
        throw fail("Factory.CreationType should not be actually called. It is just a Type signature that can be used at compile time with Typescript, by using `typeof type.CreationType`");
    }
}
BaseType.prototype.create = action(BaseType.prototype.create);
/**
 * A complex type produces a MST node (Node in the state tree)
 *
 * @internal
 * @hidden
 */
class ComplexType extends BaseType {
    identifierAttribute;
    constructor(name) {
        super(name);
    }
    create(snapshot = this.getDefaultSnapshot(), environment) {
        return super.create(snapshot, environment);
    }
    getValue(node) {
        node.createObservableInstanceIfNeeded();
        return node.storedValue;
    }
    isMatchingSnapshotId(current, snapshot) {
        return (!current.identifierAttribute ||
            current.identifier ===
                normalizeIdentifier(snapshot[current.identifierAttribute]));
    }
    tryToReconcileNode(current, newValue) {
        if (current.isDetaching) {
            return false;
        }
        if (current.snapshot === newValue) {
            // newValue is the current snapshot of the node, noop
            return true;
        }
        if (isStateTreeNode(newValue) && getStateTreeNode(newValue) === current) {
            // the current node is the same as the new one
            return true;
        }
        if (current.type === this &&
            isMutable(newValue) &&
            !isStateTreeNode(newValue) &&
            this.isMatchingSnapshotId(current, newValue)) {
            // the newValue has no node, so can be treated like a snapshot
            // we can reconcile
            current.applySnapshot(newValue);
            return true;
        }
        return false;
    }
    reconcile(current, newValue, parent, subpath) {
        const nodeReconciled = this.tryToReconcileNode(current, newValue);
        if (nodeReconciled) {
            current.setParent(parent, subpath);
            return current;
        }
        // current node cannot be recycled in any way
        current.die(); // noop if detaching
        // attempt to reuse the new one
        if (isStateTreeNode(newValue) && this.isAssignableFrom(getType(newValue))) {
            // newValue is a Node as well, move it here..
            const newNode = getStateTreeNode(newValue);
            newNode.setParent(parent, subpath);
            return newNode;
        }
        // nothing to do, we have to create a new node
        return this.instantiate(parent, subpath, undefined, newValue);
    }
    getSubTypes() {
        return null;
    }
}
ComplexType.prototype.create = action(ComplexType.prototype.create);
/**
 * @internal
 * @hidden
 */
class SimpleType extends BaseType {
    createNewInstance(snapshot) {
        return snapshot;
    }
    getValue(node) {
        // if we ever find a case where scalar nodes can be accessed without iterating through its parent
        // uncomment this to make sure the parent chain is created when this is accessed
        // if (node.parent) {
        //     node.parent.createObservableInstanceIfNeeded()
        // }
        return node.storedValue;
    }
    getSnapshot(node) {
        return node.storedValue;
    }
    reconcile(current, newValue, parent, subpath) {
        // reconcile only if type and value are still the same, and only if the node is not detaching
        if (!current.isDetaching &&
            current.type === this &&
            current.storedValue === newValue) {
            return current;
        }
        const res = this.instantiate(parent, subpath, undefined, newValue);
        current.die(); // noop if detaching
        return res;
    }
    getSubTypes() {
        return null;
    }
}
/**
 * Returns if a given value represents a type.
 *
 * @param value Value to check.
 * @returns `true` if the value is a type.
 */
function isType(value) {
    return typeof value === "object" && value && value.isType === true;
}
/**
 * @internal
 * @hidden
 */
function assertIsType(type, argNumber) {
    assertArg(type, isType, "mobx-state-tree type", argNumber);
}

const runningActions = new Map();
/**
 * Note: Consider migrating to `createActionTrackingMiddleware2`, it is easier to use.
 *
 * Convenience utility to create action based middleware that supports async processes more easily.
 * All hooks are called for both synchronous and asynchronous actions. Except that either `onSuccess` or `onFail` is called
 *
 * The create middleware tracks the process of an action (assuming it passes the `filter`).
 * `onResume` can return any value, which will be passed as second argument to any other hook. This makes it possible to keep state during a process.
 *
 * See the `atomic` middleware for an example
 *
 * @param hooks
 * @returns
 */
function createActionTrackingMiddleware(hooks) {
    return function actionTrackingMiddleware(call, next, abort) {
        switch (call.type) {
            case "action": {
                if (!hooks.filter || hooks.filter(call) === true) {
                    const context = hooks.onStart(call);
                    hooks.onResume(call, context);
                    runningActions.set(call.id, {
                        call,
                        context,
                        async: false
                    });
                    try {
                        const res = next(call);
                        hooks.onSuspend(call, context);
                        if (runningActions.get(call.id).async === false) {
                            runningActions.delete(call.id);
                            hooks.onSuccess(call, context, res);
                        }
                        return res;
                    }
                    catch (e) {
                        runningActions.delete(call.id);
                        hooks.onFail(call, context, e);
                        throw e;
                    }
                }
                else {
                    return next(call);
                }
            }
            case "flow_spawn": {
                const root = runningActions.get(call.rootId);
                root.async = true;
                return next(call);
            }
            case "flow_resume":
            case "flow_resume_error": {
                const root = runningActions.get(call.rootId);
                hooks.onResume(call, root.context);
                try {
                    return next(call);
                }
                finally {
                    hooks.onSuspend(call, root.context);
                }
            }
            case "flow_throw": {
                const root = runningActions.get(call.rootId);
                runningActions.delete(call.rootId);
                hooks.onFail(call, root.context, call.args[0]);
                return next(call);
            }
            case "flow_return": {
                const root = runningActions.get(call.rootId);
                runningActions.delete(call.rootId);
                hooks.onSuccess(call, root.context, call.args[0]);
                return next(call);
            }
        }
    };
}

class RunningAction {
    hooks;
    call;
    flowsPending = 0;
    running = true;
    constructor(hooks, call) {
        this.hooks = hooks;
        this.call = call;
        if (hooks) {
            hooks.onStart(call);
        }
    }
    finish(error) {
        if (this.running) {
            this.running = false;
            if (this.hooks) {
                this.hooks.onFinish(this.call, error);
            }
        }
    }
    incFlowsPending() {
        this.flowsPending++;
    }
    decFlowsPending() {
        this.flowsPending--;
    }
    get hasFlowsPending() {
        return this.flowsPending > 0;
    }
}
/**
 * Convenience utility to create action based middleware that supports async processes more easily.
 * The flow is like this:
 * - for each action: if filter passes -> `onStart` -> (inner actions recursively) -> `onFinish`
 *
 * Example: if we had an action `a` that called inside an action `b1`, then `b2` the flow would be:
 * - `filter(a)`
 * - `onStart(a)`
 *   - `filter(b1)`
 *   - `onStart(b1)`
 *   - `onFinish(b1)`
 *   - `filter(b2)`
 *   - `onStart(b2)`
 *   - `onFinish(b2)`
 * - `onFinish(a)`
 *
 * The flow is the same no matter if the actions are sync or async.
 *
 * See the `atomic` middleware for an example
 *
 * @param hooks
 * @returns
 */
function createActionTrackingMiddleware2(middlewareHooks) {
    const runningActions = new Map();
    return function actionTrackingMiddleware(call, next) {
        // find parentRunningAction
        const parentRunningAction = call.parentActionEvent
            ? runningActions.get(call.parentActionEvent.id)
            : undefined;
        if (call.type === "action") {
            const newCall = {
                ...call,
                // make a shallow copy of the parent action env
                env: parentRunningAction && parentRunningAction.call.env,
                parentCall: parentRunningAction && parentRunningAction.call
            };
            const passesFilter = !middlewareHooks.filter || middlewareHooks.filter(newCall);
            const hooks = passesFilter ? middlewareHooks : undefined;
            const runningAction = new RunningAction(hooks, newCall);
            runningActions.set(call.id, runningAction);
            let res;
            try {
                res = next(call);
            }
            catch (e) {
                runningActions.delete(call.id);
                runningAction.finish(e);
                throw e;
            }
            // sync action finished
            if (!runningAction.hasFlowsPending) {
                runningActions.delete(call.id);
                runningAction.finish();
            }
            return res;
        }
        else {
            if (!parentRunningAction) {
                return next(call);
            }
            switch (call.type) {
                case "flow_spawn": {
                    parentRunningAction.incFlowsPending();
                    return next(call);
                }
                case "flow_resume":
                case "flow_resume_error": {
                    return next(call);
                }
                case "flow_throw": {
                    const error = call.args[0];
                    try {
                        return next(call);
                    }
                    finally {
                        parentRunningAction.decFlowsPending();
                        if (!parentRunningAction.hasFlowsPending) {
                            runningActions.delete(call.parentActionEvent.id);
                            parentRunningAction.finish(error);
                        }
                    }
                }
                case "flow_return": {
                    try {
                        return next(call);
                    }
                    finally {
                        parentRunningAction.decFlowsPending();
                        if (!parentRunningAction.hasFlowsPending) {
                            runningActions.delete(call.parentActionEvent.id);
                            parentRunningAction.finish();
                        }
                    }
                }
            }
        }
    };
}

function serializeArgument(node, actionName, index, arg) {
    if (arg instanceof Date) {
        return { $MST_DATE: arg.getTime() };
    }
    if (isPrimitive(arg)) {
        return arg;
    }
    // We should not serialize MST nodes, even if we can, because we don't know if the receiving party can handle a raw snapshot instead of an
    // MST type instance. So if one wants to serialize a MST node that was pass in, either explitly pass: 1: an id, 2: a (relative) path, 3: a snapshot
    if (isStateTreeNode(arg)) {
        return serializeTheUnserializable(`[MSTNode: ${getType(arg).name}]`);
    }
    if (typeof arg === "function") {
        return serializeTheUnserializable(`[function]`);
    }
    if (typeof arg === "object" && !isPlainObject(arg) && !isArray(arg)) {
        return serializeTheUnserializable(`[object ${(arg && arg.constructor && arg.constructor.name) ||
            "Complex Object"}]`);
    }
    try {
        // Check if serializable, cycle free etc...
        // MWE: there must be a better way....
        JSON.stringify(arg); // or throws
        return arg;
    }
    catch (e) {
        return serializeTheUnserializable("" + e);
    }
}
function deserializeArgument(adm, value) {
    if (value && typeof value === "object" && "$MST_DATE" in value) {
        return new Date(value["$MST_DATE"]);
    }
    return value;
}
function serializeTheUnserializable(baseType) {
    return {
        $MST_UNSERIALIZABLE: true,
        type: baseType
    };
}
/**
 * Applies an action or a series of actions in a single MobX transaction.
 * Does not return any value
 * Takes an action description as produced by the `onAction` middleware.
 *
 * @param target
 * @param actions
 */
function applyAction(target, actions) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    assertArg(actions, a => typeof a === "object", "object or array", 2);
    runInAction(() => {
        asArray(actions).forEach(action => baseApplyAction(target, action));
    });
}
function baseApplyAction(target, action) {
    const resolvedTarget = tryResolve(target, action.path || "");
    if (!resolvedTarget) {
        throw fail(`Invalid action path: ${action.path || ""}`);
    }
    const node = getStateTreeNode(resolvedTarget);
    // Reserved functions
    if (action.name === "@APPLY_PATCHES") {
        return applyPatch.call(null, resolvedTarget, action.args[0]);
    }
    if (action.name === "@APPLY_SNAPSHOT") {
        return applySnapshot.call(null, resolvedTarget, action.args[0]);
    }
    if (!(typeof resolvedTarget[action.name] === "function")) {
        throw fail(`Action '${action.name}' does not exist in '${node.path}'`);
    }
    return resolvedTarget[action.name].apply(resolvedTarget, action.args ? action.args.map(v => deserializeArgument(node, v)) : []);
}
/**
 * Small abstraction around `onAction` and `applyAction`, attaches an action listener to a tree and records all the actions emitted.
 * Returns an recorder object with the following signature:
 *
 * Example:
 * ```ts
 * export interface IActionRecorder {
 *      // the recorded actions
 *      actions: ISerializedActionCall[]
 *      // true if currently recording
 *      recording: boolean
 *      // stop recording actions
 *      stop(): void
 *      // resume recording actions
 *      resume(): void
 *      // apply all the recorded actions on the given object
 *      replay(target: IAnyStateTreeNode): void
 * }
 * ```
 *
 * The optional filter function allows to skip recording certain actions.
 *
 * @param subject
 * @returns
 */
function recordActions(subject, filter) {
    // check all arguments
    assertIsStateTreeNode(subject, 1);
    const actions = [];
    const listener = (call) => {
        const recordThis = filter ? filter(call, getRunningActionContext()) : true;
        if (recordThis) {
            actions.push(call);
        }
    };
    let disposer;
    const recorder = {
        actions,
        get recording() {
            return !!disposer;
        },
        stop() {
            if (disposer) {
                disposer();
                disposer = undefined;
            }
        },
        resume() {
            if (disposer) {
                return;
            }
            disposer = onAction(subject, listener);
        },
        replay(target) {
            applyAction(target, actions);
        }
    };
    recorder.resume();
    return recorder;
}
/**
 * Registers a function that will be invoked for each action that is called on the provided model instance, or to any of its children.
 * See [actions](https://github.com/mobxjs/mobx-state-tree#actions) for more details. onAction events are emitted only for the outermost called action in the stack.
 * Action can also be intercepted by middleware using addMiddleware to change the function call before it will be run.
 *
 * Not all action arguments might be serializable. For unserializable arguments, a struct like `{ $MST_UNSERIALIZABLE: true, type: "someType" }` will be generated.
 * MST Nodes are considered non-serializable as well (they could be serialized as there snapshot, but it is uncertain whether an replaying party will be able to handle such a non-instantiated snapshot).
 * Rather, when using `onAction` middleware, one should consider in passing arguments which are 1: an id, 2: a (relative) path, or 3: a snapshot. Instead of a real MST node.
 *
 * Example:
 * ```ts
 * const Todo = types.model({
 *   task: types.string
 * })
 *
 * const TodoStore = types.model({
 *   todos: types.array(Todo)
 * }).actions(self => ({
 *   add(todo) {
 *     self.todos.push(todo);
 *   }
 * }))
 *
 * const s = TodoStore.create({ todos: [] })
 *
 * let disposer = onAction(s, (call) => {
 *   console.log(call);
 * })
 *
 * s.add({ task: "Grab a coffee" })
 * // Logs: { name: "add", path: "", args: [{ task: "Grab a coffee" }] }
 * ```
 *
 * @param target
 * @param listener
 * @param attachAfter (default false) fires the listener *after* the action has executed instead of before.
 * @returns
 */
function onAction(target, listener, attachAfter = false) {
    // check all arguments
    assertIsStateTreeNode(target, 1);
    if (devMode()) {
        if (!isRoot(target)) {
            warnError("Warning: Attaching onAction listeners to non root nodes is dangerous: No events will be emitted for actions initiated higher up in the tree.");
        }
        if (!isProtected(target)) {
            warnError("Warning: Attaching onAction listeners to non protected nodes is dangerous: No events will be emitted for direct modifications without action.");
        }
    }
    return addMiddleware(target, function handler(rawCall, next) {
        if (rawCall.type === "action" && rawCall.id === rawCall.rootId) {
            const sourceNode = getStateTreeNode(rawCall.context);
            const info = {
                name: rawCall.name,
                path: getRelativePathBetweenNodes(getStateTreeNode(target), sourceNode),
                args: rawCall.args.map((arg, index) => serializeArgument(sourceNode, rawCall.name, index, arg))
            };
            if (attachAfter) {
                const res = next(rawCall);
                listener(info);
                return res;
            }
            else {
                listener(info);
                return next(rawCall);
            }
        }
        else {
            return next(rawCall);
        }
    });
}

let nextActionId = 1;
let currentActionContext;
/**
 * @internal
 * @hidden
 */
function getCurrentActionContext() {
    return currentActionContext;
}
/**
 * @internal
 * @hidden
 */
function getNextActionId() {
    return nextActionId++;
}
// TODO: optimize away entire action context if there is no middleware in tree?
/**
 * @internal
 * @hidden
 */
function runWithActionContext(context, fn) {
    const node = getStateTreeNode(context.context);
    if (context.type === "action") {
        node.assertAlive({
            actionContext: context
        });
    }
    const baseIsRunningAction = node._isRunningAction;
    node._isRunningAction = true;
    const previousContext = currentActionContext;
    currentActionContext = context;
    try {
        return runMiddleWares(node, context, fn);
    }
    finally {
        currentActionContext = previousContext;
        node._isRunningAction = baseIsRunningAction;
    }
}
/**
 * @internal
 * @hidden
 */
function getParentActionContext(parentContext) {
    if (!parentContext) {
        return undefined;
    }
    if (parentContext.type === "action") {
        return parentContext;
    }
    return parentContext.parentActionEvent;
}
/**
 * @internal
 * @hidden
 */
function createActionInvoker(target, name, fn) {
    const res = function () {
        const id = getNextActionId();
        const parentContext = currentActionContext;
        const parentActionContext = getParentActionContext(parentContext);
        return runWithActionContext({
            type: "action",
            name,
            id,
            args: argsToArray(arguments),
            context: target,
            tree: getRoot(target),
            rootId: parentContext ? parentContext.rootId : id,
            parentId: parentContext ? parentContext.id : 0,
            allParentIds: parentContext
                ? [...parentContext.allParentIds, parentContext.id]
                : [],
            parentEvent: parentContext,
            parentActionEvent: parentActionContext
        }, fn);
    };
    res._isMSTAction = true;
    res._isFlowAction = fn._isFlowAction;
    return res;
}
/**
 * Middleware can be used to intercept any action is invoked on the subtree where it is attached.
 * If a tree is protected (by default), this means that any mutation of the tree will pass through your middleware.
 *
 * For more details, see the [middleware docs](concepts/middleware.md)
 *
 * @param target Node to apply the middleware to.
 * @param middleware Middleware to apply.
 * @returns A callable function to dispose the middleware.
 */
function addMiddleware(target, handler, includeHooks = true) {
    const node = getStateTreeNode(target);
    if (devMode()) {
        if (!node.isProtectionEnabled) {
            warnError("It is recommended to protect the state tree before attaching action middleware, as otherwise it cannot be guaranteed that all changes are passed through middleware. See `protect`");
        }
    }
    return node.addMiddleWare(handler, includeHooks);
}
/**
 * Binds middleware to a specific action.
 *
 * Example:
 * ```ts
 * type.actions(self => {
 *   function takeA____() {
 *       self.toilet.donate()
 *       self.wipe()
 *       self.wipe()
 *       self.toilet.flush()
 *   }
 *   return {
 *     takeA____: decorate(atomic, takeA____)
 *   }
 * })
 * ```
 *
 * @param handler
 * @param fn
 * @param includeHooks
 * @returns The original function
 */
function decorate(handler, fn, includeHooks = true) {
    const middleware = { handler, includeHooks };
    fn.$mst_middleware = fn.$mst_middleware || [];
    fn.$mst_middleware.push(middleware);
    return fn;
}
class CollectedMiddlewares {
    arrayIndex = 0;
    inArrayIndex = 0;
    middlewares = [];
    constructor(node, fn) {
        // we just push middleware arrays into an array of arrays to avoid making copies
        if (fn.$mst_middleware) {
            this.middlewares.push(fn.$mst_middleware);
        }
        let n = node;
        // Find all middlewares. Optimization: cache this?
        while (n) {
            if (n.middlewares) {
                this.middlewares.push(n.middlewares);
            }
            n = n.parent;
        }
    }
    get isEmpty() {
        return this.middlewares.length <= 0;
    }
    getNextMiddleware() {
        const array = this.middlewares[this.arrayIndex];
        if (!array) {
            return undefined;
        }
        const item = array[this.inArrayIndex++];
        if (!item) {
            this.arrayIndex++;
            this.inArrayIndex = 0;
            return this.getNextMiddleware();
        }
        return item;
    }
}
function runMiddleWares(node, baseCall, originalFn) {
    const middlewares = new CollectedMiddlewares(node, originalFn);
    // Short circuit
    if (middlewares.isEmpty) {
        return action(originalFn).apply(null, baseCall.args);
    }
    let result = null;
    function runNextMiddleware(call) {
        const middleware = middlewares.getNextMiddleware();
        const handler = middleware && middleware.handler;
        if (!handler) {
            return action(originalFn).apply(null, call.args);
        }
        // skip hooks if asked to
        if (!middleware.includeHooks && Hook[call.name]) {
            return runNextMiddleware(call);
        }
        let nextInvoked = false;
        function next(call2, callback) {
            nextInvoked = true;
            // the result can contain
            // - the non manipulated return value from an action
            // - the non manipulated abort value
            // - one of the above but manipulated through the callback function
            result = runNextMiddleware(call2);
            if (callback) {
                result = callback(result);
            }
        }
        let abortInvoked = false;
        function abort(value) {
            abortInvoked = true;
            // overwrite the result
            // can be manipulated through middlewares earlier in the queue using the callback fn
            result = value;
        }
        handler(call, next, abort);
        if (devMode()) {
            if (!nextInvoked && !abortInvoked) {
                const node2 = getStateTreeNode(call.tree);
                throw fail(`Neither the next() nor the abort() callback within the middleware ${handler.name} for the action: "${call.name}" on the node: ${node2.type.name} was invoked.`);
            }
            else if (nextInvoked && abortInvoked) {
                const node2 = getStateTreeNode(call.tree);
                throw fail(`The next() and abort() callback within the middleware ${handler.name} for the action: "${call.name}" on the node: ${node2.type.name} were invoked.`);
            }
        }
        return result;
    }
    return runNextMiddleware(baseCall);
}

/**
 * Returns the currently executing MST action context, or undefined if none.
 */
function getRunningActionContext() {
    let current = getCurrentActionContext();
    while (current && current.type !== "action") {
        current = current.parentActionEvent;
    }
    return current;
}
function _isActionContextThisOrChildOf(actionContext, sameOrParent, includeSame) {
    const parentId = typeof sameOrParent === "number" ? sameOrParent : sameOrParent.id;
    let current = includeSame
        ? actionContext
        : actionContext.parentActionEvent;
    while (current) {
        if (current.id === parentId) {
            return true;
        }
        current = current.parentActionEvent;
    }
    return false;
}
/**
 * Returns if the given action context is a parent of this action context.
 */
function isActionContextChildOf(actionContext, parent) {
    return _isActionContextThisOrChildOf(actionContext, parent, false);
}
/**
 * Returns if the given action context is this or a parent of this action context.
 */
function isActionContextThisOrChildOf(actionContext, parentOrThis) {
    return _isActionContextThisOrChildOf(actionContext, parentOrThis, true);
}

function safeStringify(value) {
    try {
        return JSON.stringify(value);
    }
    catch (e) {
        // istanbul ignore next
        return `<Unserializable: ${e}>`;
    }
}
/**
 * @internal
 * @hidden
 */
function prettyPrintValue(value) {
    return typeof value === "function"
        ? `<function${value.name ? " " + value.name : ""}>`
        : isStateTreeNode(value)
            ? `<${value}>`
            : `\`${safeStringify(value)}\``;
}
function shortenPrintValue(valueInString) {
    return valueInString.length < 280
        ? valueInString
        : `${valueInString.substring(0, 272)}......${valueInString.substring(valueInString.length - 8)}`;
}
function toErrorString(error) {
    const { value } = error;
    const type = error.context[error.context.length - 1].type;
    const fullPath = error.context
        .map(({ path }) => path)
        .filter(path => path.length > 0)
        .join("/");
    const pathPrefix = fullPath.length > 0 ? `at path "/${fullPath}" ` : ``;
    const currentTypename = isStateTreeNode(value)
        ? `value of type ${getStateTreeNode(value).type.name}:`
        : isPrimitive(value)
            ? "value"
            : "snapshot";
    const isSnapshotCompatible = type && isStateTreeNode(value) && type.is(getStateTreeNode(value).snapshot);
    return (`${pathPrefix}${currentTypename} ${prettyPrintValue(value)} is not assignable ${type ? `to type: \`${type.name}\`` : ``}` +
        (error.message ? ` (${error.message})` : "") +
        (type
            ? isPrimitiveType(type) || isPrimitive(value)
                ? `.`
                : `, expected an instance of \`${type.name}\` or a snapshot like \`${type.describe()}\` instead.` +
                    (isSnapshotCompatible
                        ? " (Note that a snapshot of the provided value is compatible with the targeted type)"
                        : "")
            : `.`));
}
/**
 * @internal
 * @hidden
 * Pushes a new entry onto the context array (mutates in place for performance).
 * Returns the same context array for chaining.
 */
function getContextForPath(context, path, type) {
    context.push({ path, type });
    return context;
}
/**
 * @internal
 * @hidden
 * Pops the last entry from the context array (mutates in place).
 * Must be called after validation to restore context state.
 */
function popContext(context) {
    context.pop();
}
/**
 * @internal
 * @hidden
 */
function typeCheckSuccess() {
    return EMPTY_ARRAY;
}
/**
 * @internal
 * @hidden
 */
function typeCheckFailure(context, value, message) {
    // Clone context since it may be mutated after this error is created
    return [{ context: context.slice(), value, message }];
}
/**
 * @internal
 * @hidden
 */
function flattenTypeErrors(errors) {
    return errors.flat();
}
// TODO; doublecheck: typecheck should only needed to be invoked from: type.create and array / map / value.property will change
/**
 * @internal
 * @hidden
 */
function typecheckInternal(type, value) {
    // runs typeChecking if it is in dev-mode or through a process.env.ENABLE_TYPE_CHECK flag
    if (isTypeCheckingEnabled()) {
        typecheck(type, value);
    }
}
/**
 * Run's the typechecker for the given type on the given value, which can be a snapshot or an instance.
 * Throws if the given value is not according the provided type specification.
 * Use this if you need typechecks even in a production build (by default all automatic runtime type checks will be skipped in production builds)
 *
 * @param type Type to check against.
 * @param value Value to be checked, either a snapshot or an instance.
 */
function typecheck(type, value) {
    const errors = type.validate(value, [{ path: "", type }]);
    if (errors.length > 0) {
        throw fail(validationErrorsToString(type, value, errors));
    }
}
function validationErrorsToString(type, value, errors) {
    if (errors.length === 0) {
        return undefined;
    }
    return (`Error while converting ${shortenPrintValue(prettyPrintValue(value))} to \`${type.name}\`:\n\n    ` + errors.map(toErrorString).join("\n    "));
}

let identifierCacheId = 0;
/**
 * @internal
 * @hidden
 */
class IdentifierCache {
    cacheId = identifierCacheId++;
    // n.b. in cache all identifiers are normalized to strings
    cache = observable.map();
    // last time the cache (array) for a given time changed
    // n.b. it is not really the time, but just an integer that gets increased after each modification to the array
    lastCacheModificationPerId = observable.map();
    constructor() { }
    updateLastCacheModificationPerId(identifier) {
        const lcm = this.lastCacheModificationPerId.get(identifier);
        // we start at 1 since 0 means no update since cache creation
        this.lastCacheModificationPerId.set(identifier, lcm === undefined ? 1 : lcm + 1);
    }
    getLastCacheModificationPerId(identifier) {
        const modificationId = this.lastCacheModificationPerId.get(identifier) || 0;
        return `${this.cacheId}-${modificationId}`;
    }
    addNodeToCache(node, lastCacheUpdate = true) {
        if (node.identifierAttribute) {
            const identifier = node.identifier;
            if (!this.cache.has(identifier)) {
                this.cache.set(identifier, observable.array([], mobxShallow));
            }
            const set = this.cache.get(identifier);
            if (set.indexOf(node) !== -1) {
                throw fail(`Already registered`);
            }
            set.push(node);
            if (lastCacheUpdate) {
                this.updateLastCacheModificationPerId(identifier);
            }
        }
    }
    mergeCache(node) {
        values(node.identifierCache.cache).forEach(nodes => nodes.forEach(child => {
            this.addNodeToCache(child);
        }));
    }
    notifyDied(node) {
        if (node.identifierAttribute) {
            const id = node.identifier;
            const set = this.cache.get(id);
            if (set) {
                set.remove(node);
                // remove empty sets from cache
                if (!set.length) {
                    this.cache.delete(id);
                }
                this.updateLastCacheModificationPerId(node.identifier);
            }
        }
    }
    splitCache(splitNode) {
        const newCache = new IdentifierCache();
        // The slash is added here so we only match children of the splitNode. In version 5.1.8 and
        // earlier there was no trailing slash, so non children that started with the same path string
        // were being matched incorrectly.
        const basePath = splitNode.path + "/";
        entries(this.cache).forEach(([id, nodes]) => {
            let modified = false;
            for (let i = nodes.length - 1; i >= 0; i--) {
                const node = nodes[i];
                if (node === splitNode || node.path.indexOf(basePath) === 0) {
                    newCache.addNodeToCache(node, false); // no need to update lastUpdated since it is a whole new cache
                    nodes.splice(i, 1);
                    // remove empty sets from cache
                    if (!nodes.length) {
                        this.cache.delete(id);
                    }
                    modified = true;
                }
            }
            if (modified) {
                this.updateLastCacheModificationPerId(id);
            }
        });
        return newCache;
    }
    has(type, identifier) {
        const set = this.cache.get(identifier);
        if (!set) {
            return false;
        }
        return set.some(candidate => type.isAssignableFrom(candidate.type));
    }
    resolve(type, identifier) {
        const set = this.cache.get(identifier);
        if (!set) {
            return null;
        }
        const matches = set.filter(candidate => type.isAssignableFrom(candidate.type));
        switch (matches.length) {
            case 0:
                return null;
            case 1:
                return matches[0];
            default:
                throw fail(`Cannot resolve a reference to type '${type.name}' with id: '${identifier}' unambigously, there are multiple candidates: ${matches
                    .map(n => n.path)
                    .join(", ")}`);
        }
    }
}

/**
 * @internal
 * @hidden
 */
function createObjectNode(type, parent, subpath, environment, initialValue) {
    const existingNode = getStateTreeNodeSafe(initialValue);
    if (existingNode) {
        if (existingNode.parent) {
            // istanbul ignore next
            throw fail(`Cannot add an object to a state tree if it is already part of the same or another state tree. Tried to assign an object to '${parent ? parent.path : ""}/${subpath}', but it lives already at '${existingNode.path}'`);
        }
        if (parent) {
            existingNode.setParent(parent, subpath);
        }
        // else it already has no parent since it is a pre-requisite
        return existingNode;
    }
    // not a node, a snapshot
    return new ObjectNode(type, parent, subpath, environment, initialValue);
}
/**
 * @internal
 * @hidden
 */
function createScalarNode(type, parent, subpath, environment, initialValue) {
    return new ScalarNode(type, parent, subpath, environment, initialValue);
}
/**
 * @internal
 * @hidden
 */
function isNode(value) {
    return value instanceof ScalarNode || value instanceof ObjectNode;
}

/**
 * @internal
 * @hidden
 */
var NodeLifeCycle;
(function (NodeLifeCycle) {
    NodeLifeCycle[NodeLifeCycle["INITIALIZING"] = 0] = "INITIALIZING";
    NodeLifeCycle[NodeLifeCycle["CREATED"] = 1] = "CREATED";
    NodeLifeCycle[NodeLifeCycle["FINALIZED"] = 2] = "FINALIZED";
    NodeLifeCycle[NodeLifeCycle["DETACHING"] = 3] = "DETACHING";
    NodeLifeCycle[NodeLifeCycle["DEAD"] = 4] = "DEAD"; // no coming back from this one
})(NodeLifeCycle || (NodeLifeCycle = {}));
/**
 * Returns true if the given value is a node in a state tree.
 * More precisely, that is, if the value is an instance of a
 * `types.model`, `types.array` or `types.map`.
 *
 * @param value
 * @returns true if the value is a state tree node.
 */
function isStateTreeNode(value) {
    return !!(value && value.$treenode);
}
/**
 * @internal
 * @hidden
 */
function assertIsStateTreeNode(value, argNumber) {
    assertArg(value, isStateTreeNode, "mobx-state-tree node", argNumber);
}
/**
 * @internal
 * @hidden
 */
function getStateTreeNode(value) {
    if (!isStateTreeNode(value)) {
        // istanbul ignore next
        throw fail(`Value ${value} is no MST Node`);
    }
    return value.$treenode;
}
/**
 * @internal
 * @hidden
 */
function getStateTreeNodeSafe(value) {
    return (value && value.$treenode) || null;
}
/**
 * @internal
 * @hidden
 */
function toJSON() {
    return getStateTreeNode(this).snapshot;
}
const doubleDot = (_) => "..";
/**
 * @internal
 * @hidden
 */
function getRelativePathBetweenNodes(base, target) {
    // PRE condition target is (a child of) base!
    if (base.root !== target.root) {
        throw fail(`Cannot calculate relative path: objects '${base}' and '${target}' are not part of the same object tree`);
    }
    const baseParts = splitJsonPath(base.path);
    const targetParts = splitJsonPath(target.path);
    let common = 0;
    for (; common < baseParts.length; common++) {
        if (baseParts[common] !== targetParts[common]) {
            break;
        }
    }
    // TODO: assert that no targetParts paths are "..", "." or ""!
    return (baseParts.slice(common).map(doubleDot).join("/") +
        joinJsonPath(targetParts.slice(common)));
}
/**
 * @internal
 * @hidden
 */
function resolveNodeByPath(base, path, failIfResolveFails = true) {
    return resolveNodeByPathParts(base, splitJsonPath(path), failIfResolveFails);
}
/**
 * @internal
 * @hidden
 */
function resolveNodeByPathParts(base, pathParts, failIfResolveFails = true) {
    let current = base;
    try {
        for (let i = 0; i < pathParts.length; i++) {
            const part = pathParts[i];
            if (part === "..") {
                current = current.parent;
                if (current) {
                    continue;
                } // not everything has a parent
            }
            else if (part === ".") {
                continue;
            }
            else if (current) {
                if (current instanceof ScalarNode) {
                    // check if the value of a scalar resolves to a state tree node (e.g. references)
                    // then we can continue resolving...
                    const value = current.value;
                    if (isStateTreeNode(value)) {
                        current = getStateTreeNode(value);
                        // fall through
                    }
                }
                if (current instanceof ObjectNode) {
                    const subType = current.getChildType(part);
                    if (subType) {
                        current = current.getChildNode(part);
                        if (current) {
                            continue;
                        }
                    }
                }
            }
            throw fail(`Could not resolve '${part}' in path '${joinJsonPath(pathParts.slice(0, i)) || "/"}' while resolving '${joinJsonPath(pathParts)}'`);
        }
    }
    catch (e) {
        if (!failIfResolveFails) {
            return undefined;
        }
        throw e;
    }
    return current;
}
/**
 * @internal
 * @hidden
 */
function convertChildNodesToArray(childNodes) {
    if (!childNodes) {
        return EMPTY_ARRAY;
    }
    const keys = Object.keys(childNodes);
    if (!keys.length) {
        return EMPTY_ARRAY;
    }
    const result = new Array(keys.length);
    keys.forEach((key, index) => {
        result[index] = childNodes[key];
    });
    return result;
}

// based on: https://github.com/mobxjs/mobx-utils/blob/master/src/async-action.ts
/*
    All contents of this file are deprecated.

    The term `process` has been replaced with `flow` to avoid conflicts with the
    global `process` object.

    Refer to `flow.ts` for any further changes to this implementation.
*/
const DEPRECATION_MESSAGE = "See https://github.com/mobxjs/mobx-state-tree/issues/399 for more information. " +
    "Note that the middleware event types starting with `process` now start with `flow`.";
/**
 * @hidden
 *
 * @deprecated has been renamed to `flow()`.
 * See https://github.com/mobxjs/mobx-state-tree/issues/399 for more information.
 * Note that the middleware event types starting with `process` now start with `flow`.
 *
 * @returns {Promise}
 */
function process$1(asyncAction) {
    deprecated("process", "`process()` has been renamed to `flow()`. " + DEPRECATION_MESSAGE);
    return flow(asyncAction);
}

const plainObjectString = Object.toString();
/**
 * @internal
 * @hidden
 */
const EMPTY_ARRAY = Object.freeze([]);
/**
 * @internal
 * @hidden
 */
const EMPTY_OBJECT = Object.freeze({});
/**
 * @internal
 * @hidden
 */
const mobxShallow = _getGlobalState().useProxies
    ? { deep: false }
    : { deep: false, proxy: false };
Object.freeze(mobxShallow);
/**
 * @internal
 * @hidden
 */
function fail(message = "Illegal state") {
    return new Error("[mobx-state-tree] " + message);
}
/**
 * @internal
 * @hidden
 */
function identity(_) {
    return _;
}
/**
 * @internal
 * @hidden
 */
const isInteger = Number.isInteger;
/**
 * @internal
 * @hidden
 */
function isFloat(val) {
    return Number(val) === val && val % 1 !== 0;
}
/**
 * @internal
 * @hidden
 */
function isFinite(val) {
    return Number.isFinite(val);
}
/**
 * @internal
 * @hidden
 */
function isArray(val) {
    return Array.isArray(val) || isObservableArray(val);
}
/**
 * @internal
 * @hidden
 */
function asArray(val) {
    if (!val) {
        return EMPTY_ARRAY;
    }
    if (isArray(val)) {
        return val;
    }
    return [val];
}
/**
 * @internal
 * @hidden
 */
function extend(a, ...b) {
    for (let i = 0; i < b.length; i++) {
        const current = b[i];
        for (const key in current) {
            a[key] = current[key];
        }
    }
    return a;
}
/**
 * @internal
 * @hidden
 */
function isPlainObject(value) {
    if (value === null || typeof value !== "object") {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    if (proto == null) {
        return true;
    }
    return proto.constructor?.toString() === plainObjectString;
}
/**
 * @internal
 * @hidden
 */
function isMutable(value) {
    return (value !== null &&
        typeof value === "object" &&
        !(value instanceof Date) &&
        !(value instanceof RegExp));
}
/**
 * @internal
 * @hidden
 */
function isPrimitive(value, includeDate = true) {
    return (value === null ||
        value === undefined ||
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean" ||
        (includeDate && value instanceof Date));
}
/**
 * @internal
 * @hidden
 * Freeze a value and return it (if not in production)
 */
function freeze(value) {
    if (!devMode()) {
        return value;
    }
    return isPrimitive(value) || isObservableArray(value)
        ? value
        : Object.freeze(value);
}
/**
 * @internal
 * @hidden
 * Recursively freeze a value (if not in production)
 */
function deepFreeze(value) {
    if (!devMode()) {
        return value;
    }
    freeze(value);
    if (isPlainObject(value)) {
        Object.keys(value).forEach(propKey => {
            if (!isPrimitive(value[propKey]) &&
                !Object.isFrozen(value[propKey])) {
                deepFreeze(value[propKey]);
            }
        });
    }
    return value;
}
/**
 * @internal
 * @hidden
 */
function isSerializable(value) {
    return typeof value !== "function";
}
/**
 * @internal
 * @hidden
 */
function defineProperty(object, key, descriptor) {
    isObservableObject(object)
        ? defineProperty$1(object, key, descriptor)
        : Object.defineProperty(object, key, descriptor);
}
/**
 * @internal
 * @hidden
 */
function addHiddenFinalProp(object, propName, value) {
    defineProperty(object, propName, {
        enumerable: false,
        writable: false,
        configurable: true,
        value
    });
}
/**
 * @internal
 * @hidden
 */
function addHiddenWritableProp(object, propName, value) {
    defineProperty(object, propName, {
        enumerable: false,
        writable: true,
        configurable: true,
        value
    });
}
/**
 * @internal
 * @hidden
 */
class EventHandler {
    handlers = [];
    emitting = false;
    pendingUnregisters = null;
    get hasSubscribers() {
        return this.handlers.length > 0;
    }
    register(fn, atTheBeginning = false) {
        if (atTheBeginning) {
            this.handlers.unshift(fn);
        }
        else {
            this.handlers.push(fn);
        }
        return () => {
            this.unregister(fn);
        };
    }
    has(fn) {
        return this.handlers.indexOf(fn) >= 0;
    }
    unregister(fn) {
        if (this.emitting) {
            // defer unregistration until emit is done
            if (!this.pendingUnregisters) {
                this.pendingUnregisters = [];
            }
            this.pendingUnregisters.push(fn);
            return;
        }
        const index = this.handlers.indexOf(fn);
        if (index >= 0) {
            this.handlers.splice(index, 1);
        }
    }
    clear() {
        this.handlers.length = 0;
    }
    emit(...args) {
        // use emitting flag to defer unregistrations instead of copying array
        this.emitting = true;
        try {
            for (const f of this.handlers) {
                f(...args);
            }
        }
        finally {
            this.emitting = false;
            // process any deferred unregistrations
            if (this.pendingUnregisters) {
                for (const fn of this.pendingUnregisters) {
                    const index = this.handlers.indexOf(fn);
                    if (index >= 0) {
                        this.handlers.splice(index, 1);
                    }
                }
                this.pendingUnregisters = null;
            }
        }
    }
}
/**
 * @internal
 * @hidden
 */
class EventHandlers {
    eventHandlers;
    hasSubscribers(event) {
        const handler = this.eventHandlers && this.eventHandlers[event];
        return !!handler && handler.hasSubscribers;
    }
    register(event, fn, atTheBeginning = false) {
        if (!this.eventHandlers) {
            this.eventHandlers = {};
        }
        let handler = this.eventHandlers[event];
        if (!handler) {
            handler = this.eventHandlers[event] = new EventHandler();
        }
        return handler.register(fn, atTheBeginning);
    }
    has(event, fn) {
        const handler = this.eventHandlers && this.eventHandlers[event];
        return !!handler && handler.has(fn);
    }
    unregister(event, fn) {
        const handler = this.eventHandlers && this.eventHandlers[event];
        if (handler) {
            handler.unregister(fn);
        }
    }
    clear(event) {
        if (this.eventHandlers) {
            delete this.eventHandlers[event];
        }
    }
    clearAll() {
        this.eventHandlers = undefined;
    }
    emit(event, ...args) {
        const handler = this.eventHandlers && this.eventHandlers[event];
        if (handler) {
            handler.emit(...args);
        }
    }
}
/**
 * @internal
 * @hidden
 */
function argsToArray(args) {
    const res = new Array(args.length);
    for (let i = 0; i < args.length; i++) {
        res[i] = args[i];
    }
    return res;
}
/**
 * @internal
 * @hidden
 */
function stringStartsWith(str, beginning) {
    return str.indexOf(beginning) === 0;
}
/**
 * @internal
 * @hidden
 */
const deprecated = function (id, message) {
    // skip if running production
    if (!devMode()) {
        return;
    }
    // warn if hasn't been warned before
    if (deprecated.ids && !deprecated.ids.hasOwnProperty(id)) {
        warnError("Deprecation warning: " + message);
    }
    // mark as warned to avoid duplicate warn message
    if (deprecated.ids) {
        deprecated.ids[id] = true;
    }
};
deprecated.ids = {};
/**
 * @internal
 * @hidden
 */
function warnError(msg) {
    console.warn(new Error(`[mobx-state-tree] ${msg}`));
}
/**
 * @internal
 * @hidden
 */
function isTypeCheckingEnabled() {
    return (devMode() ||
        (typeof process !== "undefined" &&
            process.env &&
            process.env.ENABLE_TYPE_CHECK === "true"));
}
let _devMode = process.env.NODE_ENV !== "production";
/**
 * @internal
 * @hidden
 */
function devMode() {
    return _devMode;
}
/**
 * @internal
 * @hidden
 */
function setDevMode(value) {
    _devMode = value;
}
/**
 * @internal
 * @hidden
 */
function assertArg(value, fn, typeName, argNumber) {
    if (devMode()) {
        if (!fn(value)) {
            // istanbul ignore next
            throw fail(`expected ${typeName} as argument ${asArray(argNumber).join(" or ")}, got ${value} instead`);
        }
    }
}
/**
 * @internal
 * @hidden
 */
function assertIsFunction(value, argNumber) {
    assertArg(value, fn => typeof fn === "function", "function", argNumber);
}
/**
 * @internal
 * @hidden
 */
function assertIsNumber(value, argNumber, min, max) {
    assertArg(value, n => typeof n === "number", "number", argNumber);
    {
        assertArg(value, n => n >= min, `number greater than ${min}`, argNumber);
    }
}
/**
 * @internal
 * @hidden
 */
function assertIsString(value, argNumber, canBeEmpty = true) {
    assertArg(value, s => typeof s === "string", "string", argNumber);
    if (!canBeEmpty) {
        assertArg(value, s => s !== "", "not empty string", argNumber);
    }
}
/**
 * @internal
 * @hidden
 */
function setImmediateWithFallback(fn) {
    if (typeof queueMicrotask === "function") {
        queueMicrotask(fn);
    }
    else if (typeof setImmediate === "function") {
        setImmediate(fn);
    }
    else {
        setTimeout(fn, 1);
    }
}

/**
 * See [asynchronous actions](concepts/async-actions.md).
 *
 * @returns The flow as a promise.
 */
function flow(generator) {
    return createFlowSpawner(generator.name, generator);
}
/**
 * @deprecated Not needed since TS3.6.
 * Used for TypeScript to make flows that return a promise return the actual promise result.
 *
 * @param val
 * @returns
 */
function castFlowReturn(val) {
    return val;
}
/**
 * @experimental
 * experimental api - might change on minor/patch releases
 *
 * Convert a promise-returning function to a generator-returning one.
 * This is intended to allow for usage of `yield*` in async actions to
 * retain the promise return type.
 *
 * Example:
 * ```ts
 * function getDataAsync(input: string): Promise<number> { ... }
 * const getDataGen = toGeneratorFunction(getDataAsync);
 *
 * const someModel.actions(self => ({
 *   someAction: flow(function*() {
 *     // value is typed as number
 *     const value = yield* getDataGen("input value");
 *     ...
 *   })
 * }))
 * ```
 */
function toGeneratorFunction(p) {
    return function* (...args) {
        return (yield p(...args));
    };
}
/**
 * @experimental
 * experimental api - might change on minor/patch releases
 *
 * Convert a promise to a generator yielding that promise
 * This is intended to allow for usage of `yield*` in async actions to
 * retain the promise return type.
 *
 * Example:
 * ```ts
 * function getDataAsync(input: string): Promise<number> { ... }
 *
 * const someModel.actions(self => ({
 *   someAction: flow(function*() {
 *     // value is typed as number
 *     const value = yield* toGenerator(getDataAsync("input value"));
 *     ...
 *   })
 * }))
 * ```
 */
function* toGenerator(p) {
    return (yield p);
}
/**
 * @internal
 * @hidden
 */
function createFlowSpawner(name, generator) {
    const spawner = function flowSpawner() {
        // Implementation based on https://github.com/tj/co/blob/master/index.js
        const runId = getNextActionId();
        const parentContext = getCurrentActionContext();
        if (!parentContext) {
            throw fail("a mst flow must always have a parent context");
        }
        const parentActionContext = getParentActionContext(parentContext);
        if (!parentActionContext) {
            throw fail("a mst flow must always have a parent action context");
        }
        const contextBase = {
            name,
            id: runId,
            tree: parentContext.tree,
            context: parentContext.context,
            parentId: parentContext.id,
            allParentIds: [...parentContext.allParentIds, parentContext.id],
            rootId: parentContext.rootId,
            parentEvent: parentContext,
            parentActionEvent: parentActionContext
        };
        const args = arguments;
        function wrap(fn, type, arg) {
            fn.$mst_middleware = spawner.$mst_middleware; // pick up any middleware attached to the flow
            return runWithActionContext({
                ...contextBase,
                type,
                args: [arg]
            }, fn);
        }
        return new Promise(function (resolve, reject) {
            let gen;
            const init = function asyncActionInit() {
                gen = generator.apply(null, arguments);
                onFulfilled(undefined); // kick off the flow
            };
            init.$mst_middleware = spawner.$mst_middleware;
            runWithActionContext({
                ...contextBase,
                type: "flow_spawn",
                args: argsToArray(args)
            }, init);
            function onFulfilled(res) {
                let ret;
                try {
                    // prettier-ignore
                    const cancelError = wrap((r) => { ret = gen.next(r); }, "flow_resume", res);
                    if (cancelError instanceof Error) {
                        ret = gen.throw(cancelError);
                    }
                }
                catch (e) {
                    // prettier-ignore
                    setImmediateWithFallback(() => {
                        wrap((r) => { reject(e); }, "flow_throw", e);
                    });
                    return;
                }
                next(ret);
                return;
            }
            function onRejected(err) {
                let ret;
                try {
                    // prettier-ignore
                    wrap((r) => { ret = gen.throw(r); }, "flow_resume_error", err); // or yieldError?
                }
                catch (e) {
                    // prettier-ignore
                    setImmediateWithFallback(() => {
                        wrap((r) => { reject(e); }, "flow_throw", e);
                    });
                    return;
                }
                next(ret);
            }
            function next(ret) {
                if (ret.done) {
                    // prettier-ignore
                    setImmediateWithFallback(() => {
                        wrap((r) => { resolve(r); }, "flow_return", ret.value);
                    });
                    return;
                }
                // TODO: support more type of values? See https://github.com/tj/co/blob/249bbdc72da24ae44076afd716349d2089b31c4c/index.js#L100
                if (!ret.value || typeof ret.value.then !== "function") {
                    // istanbul ignore next
                    throw fail("Only promises can be yielded to `async`, got: " + ret);
                }
                return ret.value.then(onFulfilled, onRejected);
            }
        });
    };
    spawner._isFlowAction = true;
    return spawner;
}

/**
 * @internal
 * @hidden
 */
function splitPatch(patch) {
    if (!("oldValue" in patch)) {
        throw fail(`Patches without \`oldValue\` field cannot be inversed`);
    }
    return [stripPatch(patch), invertPatch(patch)];
}
/**
 * @internal
 * @hidden
 */
function stripPatch(patch) {
    // strips `oldvalue` information from the patch, so that it becomes a patch conform the json-patch spec
    // this removes the ability to undo the patch
    switch (patch.op) {
        case "add":
            return { op: "add", path: patch.path, value: patch.value };
        case "remove":
            return { op: "remove", path: patch.path };
        case "replace":
            return { op: "replace", path: patch.path, value: patch.value };
    }
}
function invertPatch(patch) {
    switch (patch.op) {
        case "add":
            return {
                op: "remove",
                path: patch.path
            };
        case "remove":
            return {
                op: "add",
                path: patch.path,
                value: patch.oldValue
            };
        case "replace":
            return {
                op: "replace",
                path: patch.path,
                value: patch.oldValue
            };
    }
}
/**
 * Simple simple check to check it is a number.
 */
function isNumber(x) {
    return typeof x === "number";
}
/**
 * Escape slashes and backslashes.
 *
 * http://tools.ietf.org/html/rfc6901
 */
function escapeJsonPath(path) {
    if (isNumber(path) === true) {
        return "" + path;
    }
    if (path.indexOf("/") === -1 && path.indexOf("~") === -1) {
        return path;
    }
    return path.replace(/~/g, "~0").replace(/\//g, "~1");
}
/**
 * Unescape slashes and backslashes.
 */
function unescapeJsonPath(path) {
    return path.replace(/~1/g, "/").replace(/~0/g, "~");
}
/**
 * Generates a json-path compliant json path from path parts.
 *
 * @param path
 * @returns
 */
function joinJsonPath(path) {
    // `/` refers to property with an empty name, while `` refers to root itself!
    if (path.length === 0) {
        return "";
    }
    const getPathStr = (p) => p.map(escapeJsonPath).join("/");
    if (path[0] === "." || path[0] === "..") {
        // relative
        return getPathStr(path);
    }
    else {
        // absolute
        return "/" + getPathStr(path);
    }
}
/**
 * Splits and decodes a json path into several parts.
 *
 * @param path
 * @returns
 */
function splitJsonPath(path) {
    // `/` refers to property with an empty name, while `` refers to root itself!
    const parts = path.split("/").map(unescapeJsonPath);
    const valid = path === "" ||
        path === "." ||
        path === ".." ||
        stringStartsWith(path, "/") ||
        stringStartsWith(path, "./") ||
        stringStartsWith(path, "../");
    if (!valid) {
        throw fail(`a json path must be either rooted, empty or relative, but got '${path}'`);
    }
    // '/a/b/c' -> ["a", "b", "c"]
    // '../../b/c' -> ["..", "..", "b", "c"]
    // '' -> []
    // '/' -> ['']
    // './a' -> [".", "a"]
    // /./a' -> [".", "a"] equivalent to './a'
    if (parts[0] === "") {
        parts.shift();
    }
    return parts;
}

/** @hidden */
const $preProcessorFailed = Symbol("$preProcessorFailed");
class SnapshotProcessor extends BaseType {
    _subtype;
    _processors;
    get flags() {
        return this._subtype.flags | TypeFlags.SnapshotProcessor;
    }
    constructor(_subtype, _processors, name) {
        super(name || _subtype.name);
        this._subtype = _subtype;
        this._processors = _processors;
    }
    describe() {
        return `snapshotProcessor(${this._subtype.describe()})`;
    }
    preProcessSnapshot(sn) {
        if (this._processors.preProcessor) {
            return this._processors.preProcessor.call(null, sn);
        }
        return sn;
    }
    preProcessSnapshotSafe(sn) {
        try {
            return this.preProcessSnapshot(sn);
        }
        catch (e) {
            return $preProcessorFailed;
        }
    }
    postProcessSnapshot(sn, node) {
        if (this._processors.postProcessor) {
            return this._processors.postProcessor.call(null, sn, node.storedValue);
        }
        return sn;
    }
    _fixNode(node) {
        // the node has to use these methods rather than the original type ones
        proxyNodeTypeMethods(node.type, this, "create");
        if (node instanceof ObjectNode) {
            node.hasSnapshotPostProcessor = !!this._processors.postProcessor;
        }
        const oldGetSnapshot = node.getSnapshot;
        node.getSnapshot = () => this.postProcessSnapshot(oldGetSnapshot.call(node), node);
        if (!isUnionType(this._subtype)) {
            node.getReconciliationType = () => {
                return this;
            };
        }
    }
    instantiate(parent, subpath, environment, initialValue) {
        const processedInitialValue = isStateTreeNode(initialValue)
            ? initialValue
            : this.preProcessSnapshot(initialValue);
        const node = this._subtype.instantiate(parent, subpath, environment, processedInitialValue);
        this._fixNode(node);
        return node;
    }
    reconcile(current, newValue, parent, subpath) {
        const node = this._subtype.reconcile(current, isStateTreeNode(newValue) ? newValue : this.preProcessSnapshot(newValue), parent, subpath);
        if (node !== current) {
            this._fixNode(node);
        }
        return node;
    }
    getSnapshot(node, applyPostProcess = true) {
        const sn = this._subtype.getSnapshot(node);
        return applyPostProcess ? this.postProcessSnapshot(sn, node) : sn;
    }
    isValidSnapshot(value, context) {
        const processedSn = this.preProcessSnapshotSafe(value);
        if (processedSn === $preProcessorFailed) {
            return typeCheckFailure(context, value, "Failed to preprocess value");
        }
        return this._subtype.validate(processedSn, context);
    }
    getSubTypes() {
        return this._subtype;
    }
    is(thing) {
        const value = isType(thing)
            ? this._subtype
            : isStateTreeNode(thing)
                ? getSnapshot(thing, false)
                : this.preProcessSnapshotSafe(thing);
        if (value === $preProcessorFailed) {
            return false;
        }
        return (this._subtype.validate(value, [{ path: "", type: this._subtype }])
            .length === 0);
    }
    isAssignableFrom(type) {
        return this._subtype.isAssignableFrom(type);
    }
    isMatchingSnapshotId(current, snapshot) {
        if (!(this._subtype instanceof ComplexType)) {
            return false;
        }
        const processedSn = this.preProcessSnapshot(snapshot);
        return this._subtype.isMatchingSnapshotId(current, processedSn);
    }
}
function proxyNodeTypeMethods(nodeType, snapshotProcessorType, ...methods) {
    for (const method of methods) {
        nodeType[method] = snapshotProcessorType[method].bind(snapshotProcessorType);
    }
}
/**
 * `types.snapshotProcessor` - Runs a pre/post snapshot processor before/after serializing a given type.
 *
 * Example:
 * ```ts
 * const Todo1 = types.model({ text: types.string })
 * // in the backend the text type must be null when empty
 * interface BackendTodo {
 *     text: string | null
 * }
 *
 * const Todo2 = types.snapshotProcessor(Todo1, {
 *     // from snapshot to instance
 *     preProcessor(snapshot: BackendTodo) {
 *         return {
 *             text: sn.text || "";
 *         }
 *     },
 *
 *     // from instance to snapshot
 *     postProcessor(snapshot, node): BackendTodo {
 *         return {
 *             text: !sn.text ? null : sn.text
 *         }
 *     }
 * })
 * ```
 *
 * @param type Type to run the processors over.
 * @param processors Processors to run.
 * @param name Type name, or undefined to inherit the inner type one.
 * @returns
 */
function snapshotProcessor(type, processors, name) {
    assertIsType(type, 1);
    if (devMode()) {
        if (processors.postProcessor &&
            typeof processors.postProcessor !== "function") {
            // istanbul ignore next
            throw fail("postSnapshotProcessor must be a function");
        }
        if (processors.preProcessor &&
            typeof processors.preProcessor !== "function") {
            // istanbul ignore next
            throw fail("preSnapshotProcessor must be a function");
        }
    }
    return new SnapshotProcessor(type, processors, name);
}

const needsIdentifierError = `Map.put can only be used to store complex values that have an identifier type attribute`;
function tryCollectModelTypes(type, modelTypes) {
    const subtypes = type.getSubTypes();
    if (subtypes === cannotDetermineSubtype) {
        return false;
    }
    if (subtypes) {
        const subtypesArray = asArray(subtypes);
        for (const subtype of subtypesArray) {
            if (!tryCollectModelTypes(subtype, modelTypes)) {
                return false;
            }
        }
    }
    if (type instanceof ModelType) {
        modelTypes.push(type);
    }
    return true;
}
/**
 * @internal
 * @hidden
 */
var MapIdentifierMode;
(function (MapIdentifierMode) {
    MapIdentifierMode[MapIdentifierMode["UNKNOWN"] = 0] = "UNKNOWN";
    MapIdentifierMode[MapIdentifierMode["YES"] = 1] = "YES";
    MapIdentifierMode[MapIdentifierMode["NO"] = 2] = "NO";
})(MapIdentifierMode || (MapIdentifierMode = {}));
class MSTMap extends ObservableMap {
    constructor(initialData, name) {
        super(initialData, observable.ref.enhancer, name);
    }
    get(key) {
        // maybe this is over-enthousiastic? normalize numeric keys to strings
        return super.get("" + key);
    }
    has(key) {
        return super.has("" + key);
    }
    delete(key) {
        return super.delete("" + key);
    }
    set(key, value) {
        return super.set("" + key, value);
    }
    put(value) {
        if (!value) {
            throw fail(`Map.put cannot be used to set empty values`);
        }
        if (isStateTreeNode(value)) {
            const node = getStateTreeNode(value);
            if (devMode()) {
                if (!node.identifierAttribute) {
                    throw fail(needsIdentifierError);
                }
            }
            if (node.identifier === null) {
                throw fail(needsIdentifierError);
            }
            this.set(node.identifier, value);
            return value;
        }
        else if (!isMutable(value)) {
            throw fail(`Map.put can only be used to store complex values`);
        }
        else {
            const mapNode = getStateTreeNode(this);
            const mapType = mapNode.type;
            if (mapType.identifierMode !== MapIdentifierMode.YES) {
                throw fail(needsIdentifierError);
            }
            const idAttr = mapType.mapIdentifierAttribute;
            const id = value[idAttr];
            if (!isValidIdentifier(id)) {
                // try again but this time after creating a node for the value
                // since it might be an optional identifier
                const newNode = this.put(mapType.getChildType().create(value, mapNode.environment));
                return this.put(getSnapshot(newNode));
            }
            const key = normalizeIdentifier(id);
            this.set(key, value);
            return this.get(key);
        }
    }
}
/**
 * @internal
 * @hidden
 */
class MapType extends ComplexType {
    _subType;
    identifierMode = MapIdentifierMode.UNKNOWN;
    mapIdentifierAttribute = undefined;
    flags = TypeFlags.Map;
    hookInitializers = [];
    constructor(name, _subType, hookInitializers = []) {
        super(name);
        this._subType = _subType;
        this._determineIdentifierMode();
        this.hookInitializers = hookInitializers;
    }
    hooks(hooks) {
        const hookInitializers = this.hookInitializers.length > 0
            ? this.hookInitializers.concat(hooks)
            : [hooks];
        return new MapType(this.name, this._subType, hookInitializers);
    }
    instantiate(parent, subpath, environment, initialValue) {
        this._determineIdentifierMode();
        return createObjectNode(this, parent, subpath, environment, initialValue);
    }
    _determineIdentifierMode() {
        if (this.identifierMode !== MapIdentifierMode.UNKNOWN) {
            return;
        }
        const modelTypes = [];
        if (tryCollectModelTypes(this._subType, modelTypes)) {
            const identifierAttribute = modelTypes.reduce((current, type) => {
                if (!type.identifierAttribute) {
                    return current;
                }
                if (current && current !== type.identifierAttribute) {
                    throw fail(`The objects in a map should all have the same identifier attribute, expected '${current}', but child of type '${type.name}' declared attribute '${type.identifierAttribute}' as identifier`);
                }
                return type.identifierAttribute;
            }, undefined);
            if (identifierAttribute) {
                this.identifierMode = MapIdentifierMode.YES;
                this.mapIdentifierAttribute = identifierAttribute;
            }
            else {
                this.identifierMode = MapIdentifierMode.NO;
            }
        }
    }
    initializeChildNodes(objNode, initialSnapshot = {}) {
        const subType = objNode.type._subType;
        const result = {};
        Object.keys(initialSnapshot).forEach(name => {
            result[name] = subType.instantiate(objNode, name, undefined, initialSnapshot[name]);
        });
        return result;
    }
    createNewInstance(childNodes) {
        return new MSTMap(childNodes, this.name);
    }
    finalizeNewInstance(node, instance) {
        _interceptReads(instance, node.unbox);
        const type = node.type;
        type.hookInitializers.forEach(initializer => {
            const hooks = initializer(instance);
            Object.keys(hooks).forEach(name => {
                const hook = hooks[name];
                const actionInvoker = createActionInvoker(instance, name, hook);
                (!devMode() ? addHiddenFinalProp : addHiddenWritableProp)(instance, name, actionInvoker);
            });
        });
        intercept(instance, this.willChange);
        observe(instance, this.didChange);
    }
    describe() {
        return this.name;
    }
    getChildren(node) {
        // return (node.storedValue as ObservableMap<any>).values()
        return values(node.storedValue);
    }
    getChildNode(node, key) {
        const childNode = node.storedValue.get("" + key);
        if (!childNode) {
            throw fail("Not a child " + key);
        }
        return childNode;
    }
    willChange(change) {
        const node = getStateTreeNode(change.object);
        const key = change.name;
        node.assertWritable({ subpath: key });
        const mapType = node.type;
        const subType = mapType._subType;
        switch (change.type) {
            case "update":
                {
                    const { newValue } = change;
                    const oldValue = change.object.get(key);
                    if (newValue === oldValue) {
                        return null;
                    }
                    typecheckInternal(subType, newValue);
                    change.newValue = subType.reconcile(node.getChildNode(key), change.newValue, node, key);
                    mapType.processIdentifier(key, change.newValue);
                }
                break;
            case "add":
                {
                    typecheckInternal(subType, change.newValue);
                    change.newValue = subType.instantiate(node, key, undefined, change.newValue);
                    mapType.processIdentifier(key, change.newValue);
                }
                break;
        }
        return change;
    }
    processIdentifier(expected, node) {
        if (this.identifierMode === MapIdentifierMode.YES &&
            node instanceof ObjectNode) {
            const identifier = node.identifier;
            if (identifier !== expected) {
                throw fail(`A map of objects containing an identifier should always store the object under their own identifier. Trying to store key '${identifier}', but expected: '${expected}'`);
            }
        }
    }
    getSnapshot(node) {
        const res = {};
        node.getChildren().forEach(childNode => {
            res[childNode.subpath] = childNode.snapshot;
        });
        return res;
    }
    processInitialSnapshot(childNodes) {
        const processed = {};
        Object.keys(childNodes).forEach(key => {
            processed[key] = childNodes[key].getSnapshot();
        });
        return processed;
    }
    didChange(change) {
        const node = getStateTreeNode(change.object);
        switch (change.type) {
            case "update":
                return void node.emitPatch({
                    op: "replace",
                    path: escapeJsonPath(change.name),
                    value: change.newValue.snapshot,
                    oldValue: change.oldValue ? change.oldValue.snapshot : undefined
                }, node);
            case "add":
                return void node.emitPatch({
                    op: "add",
                    path: escapeJsonPath(change.name),
                    value: change.newValue.snapshot,
                    oldValue: undefined
                }, node);
            case "delete":
                // a node got deleted, get the old snapshot and make the node die
                const oldSnapshot = change.oldValue.snapshot;
                change.oldValue.die();
                // emit the patch
                return void node.emitPatch({
                    op: "remove",
                    path: escapeJsonPath(change.name),
                    oldValue: oldSnapshot
                }, node);
        }
    }
    applyPatchLocally(node, subpath, patch) {
        const target = node.storedValue;
        switch (patch.op) {
            case "add":
            case "replace":
                target.set(subpath, patch.value);
                break;
            case "remove":
                target.delete(subpath);
                break;
        }
    }
    applySnapshot(node, snapshot) {
        typecheckInternal(this, snapshot);
        const target = node.storedValue;
        const currentKeys = {};
        Array.from(target.keys()).forEach(key => {
            currentKeys[key] = false;
        });
        if (snapshot) {
            // Don't use target.replace, as it will throw away all existing items first
            for (const key in snapshot) {
                target.set(key, snapshot[key]);
                currentKeys["" + key] = true;
            }
        }
        Object.keys(currentKeys).forEach(key => {
            if (currentKeys[key] === false) {
                target.delete(key);
            }
        });
    }
    getChildType() {
        return this._subType;
    }
    isValidSnapshot(value, context) {
        if (!isPlainObject(value)) {
            return typeCheckFailure(context, value, "Value is not a plain object");
        }
        for (const key of Object.keys(value)) {
            getContextForPath(context, key, this._subType);
            const errors = this._subType.validate(value[key], context);
            popContext(context);
            if (errors.length > 0) {
                return errors;
            }
        }
        return typeCheckSuccess();
    }
    getDefaultSnapshot() {
        return EMPTY_OBJECT;
    }
    removeChild(node, subpath) {
        node.storedValue.delete(subpath);
    }
}
MapType.prototype.applySnapshot = action(MapType.prototype.applySnapshot);
/**
 * `types.map` - Creates a key based collection type who's children are all of a uniform declared type.
 * If the type stored in a map has an identifier, it is mandatory to store the child under that identifier in the map.
 *
 * This type will always produce [observable maps](https://mobx.js.org/api.html#observablemap)
 *
 * Example:
 * ```ts
 * const Todo = types.model({
 *   id: types.identifier,
 *   task: types.string
 * })
 *
 * const TodoStore = types.model({
 *   todos: types.map(Todo)
 * })
 *
 * const s = TodoStore.create({ todos: {} })
 * unprotect(s)
 * s.todos.set(17, { task: "Grab coffee", id: 17 })
 * s.todos.put({ task: "Grab cookie", id: 18 }) // put will infer key from the identifier
 * console.log(s.todos.get(17).task) // prints: "Grab coffee"
 * ```
 *
 * @param subtype
 * @returns
 */
function map(subtype) {
    return new MapType(`Map<string, ${subtype.name}>`, subtype);
}
/**
 * Returns if a given value represents a map type.
 *
 * @param type
 * @returns `true` if it is a map type.
 */
function isMapType(type) {
    return isType(type) && (type.flags & TypeFlags.Map) > 0;
}

/**
 * @internal
 * @hidden
 */
class ArrayType extends ComplexType {
    _subType;
    flags = TypeFlags.Array;
    hookInitializers = [];
    constructor(name, _subType, hookInitializers = []) {
        super(name);
        this._subType = _subType;
        this.hookInitializers = hookInitializers;
    }
    hooks(hooks) {
        const hookInitializers = this.hookInitializers.length > 0
            ? this.hookInitializers.concat(hooks)
            : [hooks];
        return new ArrayType(this.name, this._subType, hookInitializers);
    }
    instantiate(parent, subpath, environment, initialValue) {
        return createObjectNode(this, parent, subpath, environment, initialValue);
    }
    initializeChildNodes(objNode, snapshot = []) {
        const subType = objNode.type._subType;
        const result = {};
        snapshot.forEach((item, index) => {
            const subpath = "" + index;
            result[subpath] = subType.instantiate(objNode, subpath, undefined, item);
        });
        return result;
    }
    createNewInstance(childNodes) {
        const options = { ...mobxShallow, name: this.name };
        return observable.array(convertChildNodesToArray(childNodes), options);
    }
    finalizeNewInstance(node, instance) {
        _getAdministration(instance).dehancer = node.unbox;
        const type = node.type;
        type.hookInitializers.forEach(initializer => {
            const hooks = initializer(instance);
            Object.keys(hooks).forEach(name => {
                const hook = hooks[name];
                const actionInvoker = createActionInvoker(instance, name, hook);
                (!devMode() ? addHiddenFinalProp : addHiddenWritableProp)(instance, name, actionInvoker);
            });
        });
        intercept(instance, this.willChange);
        observe(instance, this.didChange);
    }
    describe() {
        return this.name;
    }
    getChildren(node) {
        return node.storedValue.slice();
    }
    getChildNode(node, key) {
        const index = Number(key);
        if (index < node.storedValue.length) {
            return node.storedValue[index];
        }
        throw fail("Not a child: " + key);
    }
    willChange(change) {
        const node = getStateTreeNode(change.object);
        node.assertWritable({ subpath: "" + change.index });
        const subType = node.type._subType;
        const childNodes = node.getChildren();
        switch (change.type) {
            case "update":
                {
                    if (change.newValue === change.object[change.index]) {
                        return null;
                    }
                    const updatedNodes = reconcileArrayChildren(node, subType, [childNodes[change.index]], [change.newValue], [change.index]);
                    if (!updatedNodes) {
                        return null;
                    }
                    change.newValue = updatedNodes[0];
                }
                break;
            case "splice":
                {
                    const { index, removedCount, added } = change;
                    const addedNodes = reconcileArrayChildren(node, subType, childNodes.slice(index, index + removedCount), added, added.map((_, i) => index + i));
                    if (!addedNodes) {
                        return null;
                    }
                    change.added = addedNodes;
                    // update paths of remaining items
                    for (let i = index + removedCount; i < childNodes.length; i++) {
                        childNodes[i].setParent(node, "" + (i + added.length - removedCount));
                    }
                }
                break;
        }
        return change;
    }
    getSnapshot(node) {
        return node.getChildren().map(childNode => childNode.snapshot);
    }
    processInitialSnapshot(childNodes) {
        const processed = [];
        Object.keys(childNodes).forEach(key => {
            processed.push(childNodes[key].getSnapshot());
        });
        return processed;
    }
    didChange(change) {
        const node = getStateTreeNode(change.object);
        switch (change.type) {
            case "update":
                return void node.emitPatch({
                    op: "replace",
                    path: "" + change.index,
                    value: change.newValue.snapshot,
                    oldValue: change.oldValue ? change.oldValue.snapshot : undefined
                }, node);
            case "splice":
                for (let i = change.removedCount - 1; i >= 0; i--) {
                    node.emitPatch({
                        op: "remove",
                        path: "" + (change.index + i),
                        oldValue: change.removed[i].snapshot
                    }, node);
                }
                for (let i = 0; i < change.addedCount; i++) {
                    node.emitPatch({
                        op: "add",
                        path: "" + (change.index + i),
                        value: node.getChildNode("" + (change.index + i)).snapshot,
                        oldValue: undefined
                    }, node);
                }
                return;
        }
    }
    applyPatchLocally(node, subpath, patch) {
        const target = node.storedValue;
        const index = subpath === "-" ? target.length : Number(subpath);
        switch (patch.op) {
            case "replace":
                target[index] = patch.value;
                break;
            case "add":
                target.splice(index, 0, patch.value);
                break;
            case "remove":
                target.splice(index, 1);
                break;
        }
    }
    applySnapshot(node, snapshot) {
        typecheckInternal(this, snapshot);
        const target = node.storedValue;
        target.replace(snapshot);
    }
    getChildType() {
        return this._subType;
    }
    isValidSnapshot(value, context) {
        if (!isArray(value)) {
            return typeCheckFailure(context, value, "Value is not an array");
        }
        for (let i = 0; i < value.length; i++) {
            getContextForPath(context, "" + i, this._subType);
            const errors = this._subType.validate(value[i], context);
            popContext(context);
            if (errors.length > 0) {
                return errors;
            }
        }
        return typeCheckSuccess();
    }
    getDefaultSnapshot() {
        return EMPTY_ARRAY;
    }
    removeChild(node, subpath) {
        node.storedValue.splice(Number(subpath), 1);
    }
}
ArrayType.prototype.applySnapshot = action(ArrayType.prototype.applySnapshot);
/**
 * `types.array` - Creates an index based collection type who's children are all of a uniform declared type.
 *
 * This type will always produce [observable arrays](https://mobx.js.org/api.html#observablearray)
 *
 * Example:
 * ```ts
 * const Todo = types.model({
 *   task: types.string
 * })
 *
 * const TodoStore = types.model({
 *   todos: types.array(Todo)
 * })
 *
 * const s = TodoStore.create({ todos: [] })
 * unprotect(s) // needed to allow modifying outside of an action
 * s.todos.push({ task: "Grab coffee" })
 * console.log(s.todos[0]) // prints: "Grab coffee"
 * ```
 *
 * @param subtype
 * @returns
 */
function array(subtype) {
    assertIsType(subtype, 1);
    return new ArrayType(`${subtype.name}[]`, subtype);
}
function reconcileArrayChildren(parent, childType, oldNodes, newValues, newPaths) {
    let nothingChanged = true;
    for (let i = 0;; i++) {
        const hasNewNode = i <= newValues.length - 1;
        const oldNode = oldNodes[i];
        let newValue = hasNewNode ? newValues[i] : undefined;
        const newPath = "" + newPaths[i];
        // for some reason, instead of newValue we got a node, fallback to the storedValue
        // TODO: https://github.com/mobxjs/mobx-state-tree/issues/340#issuecomment-325581681
        if (isNode(newValue)) {
            newValue = newValue.storedValue;
        }
        if (!oldNode && !hasNewNode) {
            // both are empty, end
            break;
        }
        else if (!hasNewNode) {
            // new one does not exists
            nothingChanged = false;
            oldNodes.splice(i, 1);
            if (oldNode instanceof ObjectNode) {
                // since it is going to be returned by pop/splice/shift better create it before killing it
                // so it doesn't end up in an undead state
                oldNode.createObservableInstanceIfNeeded();
            }
            oldNode.die();
            i--;
        }
        else if (!oldNode) {
            // there is no old node, create it
            // check if already belongs to the same parent. if so, avoid pushing item in. only swapping can occur.
            if (isStateTreeNode(newValue) &&
                getStateTreeNode(newValue).parent === parent) {
                // this node is owned by this parent, but not in the reconcilable set, so it must be double
                throw fail(`Cannot add an object to a state tree if it is already part of the same or another state tree. Tried to assign an object to '${parent.path}/${newPath}', but it lives already at '${getStateTreeNode(newValue).path}'`);
            }
            nothingChanged = false;
            const newNode = valueAsNode(childType, parent, newPath, newValue);
            oldNodes.splice(i, 0, newNode);
        }
        else if (areSame(oldNode, newValue)) {
            // both are the same, reconcile
            oldNodes[i] = valueAsNode(childType, parent, newPath, newValue, oldNode);
        }
        else {
            // nothing to do, try to reorder
            let oldMatch = undefined;
            // find a possible candidate to reuse
            for (let j = i; j < oldNodes.length; j++) {
                if (areSame(oldNodes[j], newValue)) {
                    oldMatch = oldNodes.splice(j, 1)[0];
                    break;
                }
            }
            nothingChanged = false;
            const newNode = valueAsNode(childType, parent, newPath, newValue, oldMatch);
            oldNodes.splice(i, 0, newNode);
        }
    }
    return nothingChanged ? null : oldNodes;
}
/**
 * Convert a value to a node at given parent and subpath. Attempts to reuse old node if possible and given.
 */
function valueAsNode(childType, parent, subpath, newValue, oldNode) {
    // ensure the value is valid-ish
    typecheckInternal(childType, newValue);
    function getNewNode() {
        // the new value has a MST node
        if (isStateTreeNode(newValue)) {
            const childNode = getStateTreeNode(newValue);
            childNode.assertAlive(EMPTY_OBJECT);
            // the node lives here
            if (childNode.parent !== null && childNode.parent === parent) {
                childNode.setParent(parent, subpath);
                return childNode;
            }
        }
        // there is old node and new one is a value/snapshot
        if (oldNode) {
            return childType.reconcile(oldNode, newValue, parent, subpath);
        }
        // nothing to do, create from scratch
        return childType.instantiate(parent, subpath, undefined, newValue);
    }
    const newNode = getNewNode();
    if (oldNode && oldNode !== newNode) {
        if (oldNode instanceof ObjectNode) {
            // since it is going to be returned by pop/splice/shift better create it before killing it
            // so it doesn't end up in an undead state
            oldNode.createObservableInstanceIfNeeded();
        }
        oldNode.die();
    }
    return newNode;
}
/**
 * Check if a node holds a value.
 */
function areSame(oldNode, newValue) {
    // never consider dead old nodes for reconciliation
    if (!oldNode.isAlive) {
        return false;
    }
    // the new value has the same node
    if (isStateTreeNode(newValue)) {
        const newNode = getStateTreeNode(newValue);
        return newNode.isAlive && newNode === oldNode;
    }
    // the provided value is the snapshot of the old node
    if (oldNode.snapshot === newValue) {
        return true;
    }
    // Non object nodes don't get reconciled
    if (!(oldNode instanceof ObjectNode)) {
        return false;
    }
    const oldNodeType = oldNode.getReconciliationType();
    // new value is a snapshot with the correct identifier
    return (oldNode.identifier !== null &&
        oldNode.identifierAttribute &&
        isPlainObject(newValue) &&
        oldNodeType.is(newValue) &&
        oldNodeType.isMatchingSnapshotId(oldNode, newValue));
}
/**
 * Returns if a given value represents an array type.
 *
 * @param type
 * @returns `true` if the type is an array type.
 */
function isArrayType(type) {
    return isType(type) && (type.flags & TypeFlags.Array) > 0;
}

const PRE_PROCESS_SNAPSHOT = "preProcessSnapshot";
const POST_PROCESS_SNAPSHOT = "postProcessSnapshot";
function objectTypeToString() {
    return getStateTreeNode(this).toString();
}
const defaultObjectOptions = {
    name: "AnonymousModel",
    properties: {},
    initializers: EMPTY_ARRAY
};
function toPropertiesObject(declaredProps) {
    const keysList = Object.keys(declaredProps);
    const alreadySeenKeys = new Set();
    keysList.forEach(key => {
        if (alreadySeenKeys.has(key)) {
            throw fail(`${key} is declared twice in the model. Model should not contain the same keys`);
        }
        alreadySeenKeys.add(key);
    });
    // loop through properties and ensures that all items are types
    return keysList.reduce((props, key) => {
        // warn if user intended a HOOK
        if (key in Hook) {
            throw fail(`Hook '${key}' was defined as property. Hooks should be defined as part of the actions`);
        }
        // the user intended to use a view
        const descriptor = Object.getOwnPropertyDescriptor(declaredProps, key);
        if ("get" in descriptor) {
            throw fail("Getters are not supported as properties. Please use views instead");
        }
        // undefined and null are not valid
        const value = descriptor.value;
        if (value === null || value === undefined) {
            throw fail("The default value of an attribute cannot be null or undefined as the type cannot be inferred. Did you mean `types.maybe(someType)`?");
        }
        // its a primitive, convert to its type
        else if (isPrimitive(value)) {
            props[key] = optional(getPrimitiveFactoryFromValue(value), value);
        }
        // map defaults to empty object automatically for models
        else if (value instanceof MapType) {
            props[key] = optional(value, {});
        }
        else if (value instanceof ArrayType) {
            props[key] = optional(value, []);
        }
        // its already a type
        else if (isType(value)) ;
        // its a function, maybe the user wanted a view?
        else if (devMode() && typeof value === "function") {
            throw fail(`Invalid type definition for property '${key}', it looks like you passed a function. Did you forget to invoke it, or did you intend to declare a view / action?`);
        }
        // no other complex values
        else if (devMode() && typeof value === "object") {
            throw fail(`Invalid type definition for property '${key}', it looks like you passed an object. Try passing another model type or a types.frozen.`);
        }
        else {
            throw fail(`Invalid type definition for property '${key}', cannot infer a type from a value like '${value}' (${typeof value})`);
        }
        return props;
    }, { ...declaredProps });
}
/**
 * @internal
 * @hidden
 */
class ModelType extends ComplexType {
    flags = TypeFlags.Object;
    /*
     * The original object definition
     */
    initializers;
    properties;
    preProcessor;
    postProcessor;
    propertyNames;
    constructor(opts) {
        super(opts.name || defaultObjectOptions.name);
        Object.assign(this, defaultObjectOptions, opts);
        // ensures that any default value gets converted to its related type
        this.properties = toPropertiesObject(this.properties);
        freeze(this.properties); // make sure nobody messes with it
        this.propertyNames = Object.keys(this.properties);
        this.identifierAttribute = this._getIdentifierAttribute();
    }
    _getIdentifierAttribute() {
        let identifierAttribute = undefined;
        this.forAllProps((propName, propType) => {
            if (propType.flags & TypeFlags.Identifier) {
                if (identifierAttribute) {
                    throw fail(`Cannot define property '${propName}' as object identifier, property '${identifierAttribute}' is already defined as identifier property`);
                }
                identifierAttribute = propName;
            }
        });
        return identifierAttribute;
    }
    cloneAndEnhance(opts) {
        return new ModelType({
            name: opts.name || this.name,
            properties: Object.assign({}, this.properties, opts.properties),
            initializers: this.initializers.concat(opts.initializers || []),
            preProcessor: opts.preProcessor || this.preProcessor,
            postProcessor: opts.postProcessor || this.postProcessor
        });
    }
    actions(fn) {
        const actionInitializer = (self) => {
            this.instantiateActions(self, fn(self));
            return self;
        };
        return this.cloneAndEnhance({ initializers: [actionInitializer] });
    }
    instantiateActions(self, actions) {
        // check if return is correct
        if (!isPlainObject(actions)) {
            throw fail(`actions initializer should return a plain object containing actions`);
        }
        // bind actions to the object created
        Object.keys(actions).forEach(name => {
            // warn if preprocessor was given
            if (name === PRE_PROCESS_SNAPSHOT) {
                throw fail(`Cannot define action '${PRE_PROCESS_SNAPSHOT}', it should be defined using 'type.preProcessSnapshot(fn)' instead`);
            }
            // warn if postprocessor was given
            if (name === POST_PROCESS_SNAPSHOT) {
                throw fail(`Cannot define action '${POST_PROCESS_SNAPSHOT}', it should be defined using 'type.postProcessSnapshot(fn)' instead`);
            }
            let action2 = actions[name];
            // apply hook composition
            const baseAction = self[name];
            if (name in Hook && baseAction) {
                const specializedAction = action2;
                action2 = function () {
                    baseAction.apply(null, arguments);
                    specializedAction.apply(null, arguments);
                };
            }
            // the goal of this is to make sure actions using "this" can call themselves,
            // while still allowing the middlewares to register them
            const middlewares = action2.$mst_middleware; // make sure middlewares are not lost
            const boundAction = action2.bind(actions);
            boundAction._isFlowAction =
                action2._isFlowAction || false;
            boundAction.$mst_middleware = middlewares;
            const actionInvoker = createActionInvoker(self, name, boundAction);
            actions[name] = actionInvoker;
            (!devMode() ? addHiddenFinalProp : addHiddenWritableProp)(self, name, actionInvoker);
        });
    }
    named = name => {
        return this.cloneAndEnhance({ name });
    };
    props = properties => {
        return this.cloneAndEnhance({ properties });
    };
    volatile(fn) {
        if (typeof fn !== "function") {
            throw fail(`You passed an ${typeof fn} to volatile state as an argument, when function is expected`);
        }
        const stateInitializer = (self) => {
            this.instantiateVolatileState(self, fn(self));
            return self;
        };
        return this.cloneAndEnhance({ initializers: [stateInitializer] });
    }
    instantiateVolatileState(self, state) {
        // check views return
        if (!isPlainObject(state)) {
            throw fail(`volatile state initializer should return a plain object containing state`);
        }
        set(self, state);
    }
    extend(fn) {
        const initializer = (self) => {
            const { actions, views, state, ...rest } = fn(self);
            for (const key in rest) {
                throw fail(`The \`extend\` function should return an object with a subset of the fields 'actions', 'views' and 'state'. Found invalid key '${key}'`);
            }
            if (state) {
                this.instantiateVolatileState(self, state);
            }
            if (views) {
                this.instantiateViews(self, views);
            }
            if (actions) {
                this.instantiateActions(self, actions);
            }
            return self;
        };
        return this.cloneAndEnhance({ initializers: [initializer] });
    }
    views(fn) {
        const viewInitializer = (self) => {
            this.instantiateViews(self, fn(self));
            return self;
        };
        return this.cloneAndEnhance({ initializers: [viewInitializer] });
    }
    instantiateViews(self, views) {
        // check views return
        if (!isPlainObject(views)) {
            throw fail(`views initializer should return a plain object containing views`);
        }
        Object.getOwnPropertyNames(views).forEach(key => {
            // is this a computed property?
            const descriptor = Object.getOwnPropertyDescriptor(views, key);
            if ("get" in descriptor) {
                defineProperty$1(self, key, descriptor);
                makeObservable(self, { [key]: computed });
            }
            else if (typeof descriptor.value === "function") {
                (!devMode() ? addHiddenFinalProp : addHiddenWritableProp)(self, key, descriptor.value);
            }
            else {
                throw fail(`A view member should either be a function or getter based property`);
            }
        });
    }
    preProcessSnapshot = preProcessor => {
        const currentPreprocessor = this.preProcessor;
        if (!currentPreprocessor) {
            return this.cloneAndEnhance({ preProcessor });
        }
        else {
            return this.cloneAndEnhance({
                preProcessor: snapshot => currentPreprocessor(preProcessor(snapshot))
            });
        }
    };
    postProcessSnapshot = postProcessor => {
        const currentPostprocessor = this.postProcessor;
        if (!currentPostprocessor) {
            return this.cloneAndEnhance({ postProcessor });
        }
        else {
            return this.cloneAndEnhance({
                postProcessor: snapshot => postProcessor(currentPostprocessor(snapshot))
            });
        }
    };
    instantiate(parent, subpath, environment, initialValue) {
        const value = isStateTreeNode(initialValue)
            ? initialValue
            : this.applySnapshotPreProcessor(initialValue);
        return createObjectNode(this, parent, subpath, environment, value);
        // Optimization: record all prop- view- and action names after first construction, and generate an optimal base class
        // that pre-reserves all these fields for fast object-member lookups
    }
    initializeChildNodes(objNode, initialSnapshot = {}) {
        const type = objNode.type;
        const result = {};
        type.forAllProps((name, childType) => {
            result[name] = childType.instantiate(objNode, name, undefined, initialSnapshot[name]);
        });
        return result;
    }
    createNewInstance(childNodes) {
        const options = { ...mobxShallow, name: this.name };
        return observable.object(childNodes, EMPTY_OBJECT, options);
    }
    finalizeNewInstance(node, instance) {
        addHiddenFinalProp(instance, "toString", objectTypeToString);
        this.forAllProps(name => {
            _interceptReads(instance, name, node.unbox);
        });
        this.initializers.reduce((self, fn) => fn(self), instance);
        intercept(instance, this.willChange);
        observe(instance, this.didChange);
    }
    willChange(chg) {
        // TODO: mobx typings don't seem to take into account that newValue can be set even when removing a prop
        const change = chg;
        const node = getStateTreeNode(change.object);
        const subpath = change.name;
        node.assertWritable({ subpath });
        const childType = node.type.properties[subpath];
        // only properties are typed, state are stored as-is references
        if (childType) {
            typecheckInternal(childType, change.newValue);
            change.newValue = childType.reconcile(node.getChildNode(subpath), change.newValue, node, subpath);
        }
        return change;
    }
    didChange(chg) {
        // TODO: mobx typings don't seem to take into account that newValue can be set even when removing a prop
        const change = chg;
        const childNode = getStateTreeNode(change.object);
        const childType = childNode.type.properties[change.name];
        if (!childType) {
            // don't emit patches for volatile state
            return;
        }
        const oldChildValue = change.oldValue ? change.oldValue.snapshot : undefined;
        childNode.emitPatch({
            op: "replace",
            path: escapeJsonPath(change.name),
            value: change.newValue.snapshot,
            oldValue: oldChildValue
        }, childNode);
    }
    getChildren(node) {
        const res = [];
        this.forAllProps(name => {
            res.push(this.getChildNode(node, name));
        });
        return res;
    }
    getChildNode(node, key) {
        if (!(key in this.properties)) {
            throw fail("Not a value property: " + key);
        }
        const adm = _getAdministration(node.storedValue, key);
        const childNode = adm.raw?.();
        if (!childNode) {
            throw fail("Node not available for property " + key);
        }
        return childNode;
    }
    getSnapshot(node, applyPostProcess = true) {
        const res = {};
        this.forAllProps((name, type) => {
            try {
                // TODO: FIXME, make sure the observable ref is used!
                const atom = getAtom(node.storedValue, name);
                atom.reportObserved();
            }
            catch (e) {
                throw fail(`${name} property is declared twice`);
            }
            res[name] = this.getChildNode(node, name).snapshot;
        });
        if (applyPostProcess) {
            return this.applySnapshotPostProcessor(res);
        }
        return res;
    }
    processInitialSnapshot(childNodes) {
        const processed = {};
        Object.keys(childNodes).forEach(key => {
            processed[key] = childNodes[key].getSnapshot();
        });
        return this.applySnapshotPostProcessor(processed);
    }
    applyPatchLocally(node, subpath, patch) {
        if (!(patch.op === "replace" || patch.op === "add")) {
            throw fail(`object does not support operation ${patch.op}`);
        }
        node.storedValue[subpath] = patch.value;
    }
    applySnapshot(node, snapshot) {
        typecheckInternal(this, snapshot);
        const preProcessedSnapshot = this.applySnapshotPreProcessor(snapshot);
        this.forAllProps(name => {
            node.storedValue[name] = preProcessedSnapshot[name];
        });
    }
    applySnapshotPreProcessor(snapshot) {
        const processor = this.preProcessor;
        return processor ? processor.call(null, snapshot) : snapshot;
    }
    applySnapshotPostProcessor(snapshot) {
        const postProcessor = this.postProcessor;
        if (postProcessor) {
            return postProcessor.call(null, snapshot);
        }
        return snapshot;
    }
    getChildType(propertyName) {
        assertIsString(propertyName, 1);
        return this.properties[propertyName];
    }
    isValidSnapshot(value, context) {
        const snapshot = this.applySnapshotPreProcessor(value);
        if (!isPlainObject(snapshot)) {
            return typeCheckFailure(context, snapshot, "Value is not a plain object");
        }
        for (const key of this.propertyNames) {
            const propType = this.properties[key];
            getContextForPath(context, key, propType);
            const errors = propType.validate(snapshot[key], context);
            popContext(context);
            if (errors.length > 0) {
                return errors;
            }
        }
        return typeCheckSuccess();
    }
    forAllProps(fn) {
        this.propertyNames.forEach(key => fn(key, this.properties[key]));
    }
    describe() {
        // optimization: cache
        return ("{ " +
            this.propertyNames
                .map(key => key + ": " + this.properties[key].describe())
                .join("; ") +
            " }");
    }
    getDefaultSnapshot() {
        return EMPTY_OBJECT;
    }
    removeChild(node, subpath) {
        node.storedValue[subpath] = undefined;
    }
}
ModelType.prototype.applySnapshot = action(ModelType.prototype.applySnapshot);
/**
 * `types.model` - Creates a new model type by providing a name, properties, volatile state and actions.
 *
 * See the [model type](/concepts/trees#creating-models) description or the [getting started](intro/getting-started.md#getting-started-1) tutorial.
 */
function model(...args) {
    if (devMode() && typeof args[0] !== "string" && args[1]) {
        throw fail("Model creation failed. First argument must be a string when two arguments are provided");
    }
    const name = typeof args[0] === "string" ? args.shift() : "AnonymousModel";
    const properties = args.shift() || {};
    return new ModelType({ name, properties });
}
/**
 * `types.compose` - Composes a new model from one or more existing model types.
 * This method can be invoked in two forms:
 * Given 2 or more model types, the types are composed into a new Type.
 * Given first parameter as a string and 2 or more model types,
 * the types are composed into a new Type with the given name
 */
function compose(...args) {
    // TODO: just join the base type names if no name is provided
    const hasTypename = typeof args[0] === "string";
    const typeName = hasTypename ? args[0] : "AnonymousModel";
    if (hasTypename) {
        args.shift();
    }
    // check all parameters
    if (devMode()) {
        args.forEach((type, i) => {
            assertArg(type, isModelType, "mobx-state-tree model type", hasTypename ? i + 2 : i + 1);
        });
    }
    return args
        .reduce((prev, cur) => prev.cloneAndEnhance({
        name: prev.name + "_" + cur.name,
        properties: cur.properties,
        initializers: cur.initializers,
        preProcessor: (snapshot) => cur.applySnapshotPreProcessor(prev.applySnapshotPreProcessor(snapshot)),
        postProcessor: (snapshot) => cur.applySnapshotPostProcessor(prev.applySnapshotPostProcessor(snapshot))
    }))
        .named(typeName);
}
/**
 * Returns if a given value represents a model type.
 *
 * @param type
 * @returns
 */
function isModelType(type) {
    return isType(type) && (type.flags & TypeFlags.Object) > 0;
}

// TODO: implement CoreType using types.custom ?
/**
 * @internal
 * @hidden
 */
class CoreType extends SimpleType {
    flags;
    checker;
    initializer;
    constructor(name, flags, checker, initializer = identity) {
        super(name);
        this.flags = flags;
        this.checker = checker;
        this.initializer = initializer;
        this.flags = flags;
    }
    describe() {
        return this.name;
    }
    instantiate(parent, subpath, environment, initialValue) {
        return createScalarNode(this, parent, subpath, environment, initialValue);
    }
    createNewInstance(snapshot) {
        return this.initializer(snapshot);
    }
    isValidSnapshot(value, context) {
        if (isPrimitive(value) && this.checker(value)) {
            return typeCheckSuccess();
        }
        const typeName = this.name === "Date" ? "Date or a unix milliseconds timestamp" : this.name;
        return typeCheckFailure(context, value, `Value is not a ${typeName}`);
    }
}
/**
 * `types.string` - Creates a type that can only contain a string value.
 * This type is used for string values by default
 *
 * Example:
 * ```ts
 * const Person = types.model({
 *   firstName: types.string,
 *   lastName: "Doe"
 * })
 * ```
 */
// tslint:disable-next-line:variable-name
const string = new CoreType("string", TypeFlags.String, v => typeof v === "string");
/**
 * `types.number` - Creates a type that can only contain a numeric value.
 * This type is used for numeric values by default
 *
 * Example:
 * ```ts
 * const Vector = types.model({
 *   x: types.number,
 *   y: 1.5
 * })
 * ```
 */
// tslint:disable-next-line:variable-name
const number = new CoreType("number", TypeFlags.Number, v => typeof v === "number");
/**
 * `types.integer` - Creates a type that can only contain an integer value.
 *
 * Example:
 * ```ts
 * const Size = types.model({
 *   width: types.integer,
 *   height: 10
 * })
 * ```
 */
// tslint:disable-next-line:variable-name
const integer = new CoreType("integer", TypeFlags.Integer, v => isInteger(v));
/**
 * `types.float` - Creates a type that can only contain an float value.
 *
 * Example:
 * ```ts
 * const Size = types.model({
 *   width: types.float,
 *   height: 10
 * })
 * ```
 */
// tslint:disable-next-line:variable-name
const float = new CoreType("float", TypeFlags.Float, v => isFloat(v));
/**
 * `types.finite` - Creates a type that can only contain an finite value.
 *
 * Example:
 * ```ts
 * const Size = types.model({
 *   width: types.finite,
 *   height: 10
 * })
 * ```
 */
// tslint:disable-next-line:variable-name
const finite = new CoreType("finite", TypeFlags.Finite, v => isFinite(v));
/**
 * `types.boolean` - Creates a type that can only contain a boolean value.
 * This type is used for boolean values by default
 *
 * Example:
 * ```ts
 * const Thing = types.model({
 *   isCool: types.boolean,
 *   isAwesome: false
 * })
 * ```
 */
// tslint:disable-next-line:variable-name
const boolean = new CoreType("boolean", TypeFlags.Boolean, v => typeof v === "boolean");
/**
 * `types.null` - The type of the value `null`
 */
const nullType = new CoreType("null", TypeFlags.Null, v => v === null);
/**
 * `types.undefined` - The type of the value `undefined`
 */
const undefinedType = new CoreType("undefined", TypeFlags.Undefined, v => v === undefined);
const _DatePrimitive = new CoreType("Date", TypeFlags.Date, v => typeof v === "number" || v instanceof Date, v => (v instanceof Date ? v : new Date(v)));
_DatePrimitive.getSnapshot = function (node) {
    return node.storedValue.getTime();
};
/**
 * `types.Date` - Creates a type that can only contain a javascript Date value.
 *
 * Example:
 * ```ts
 * const LogLine = types.model({
 *   timestamp: types.Date,
 * })
 *
 * LogLine.create({ timestamp: new Date() })
 * ```
 */
const DatePrimitive = _DatePrimitive;
/**
 * @internal
 * @hidden
 */
function getPrimitiveFactoryFromValue(value) {
    switch (typeof value) {
        case "string":
            return string;
        case "number":
            return number; // In the future, isInteger(value) ? integer : number would be interesting, but would be too breaking for now
        case "boolean":
            return boolean;
        case "object":
            if (value instanceof Date) {
                return DatePrimitive;
            }
    }
    throw fail("Cannot determine primitive type from value " + value);
}
/**
 * Returns if a given value represents a primitive type.
 *
 * @param type
 * @returns
 */
function isPrimitiveType(type) {
    return (isType(type) &&
        (type.flags &
            (TypeFlags.String |
                TypeFlags.Number |
                TypeFlags.Integer |
                TypeFlags.Boolean |
                TypeFlags.Date)) >
            0);
}

/**
 * @internal
 * @hidden
 */
class Literal extends SimpleType {
    value;
    flags = TypeFlags.Literal;
    constructor(value) {
        super(JSON.stringify(value));
        this.value = value;
    }
    instantiate(parent, subpath, environment, initialValue) {
        return createScalarNode(this, parent, subpath, environment, initialValue);
    }
    describe() {
        return JSON.stringify(this.value);
    }
    isValidSnapshot(value, context) {
        if (isPrimitive(value) && value === this.value) {
            return typeCheckSuccess();
        }
        return typeCheckFailure(context, value, `Value is not a literal ${JSON.stringify(this.value)}`);
    }
}
/**
 * `types.literal` - The literal type will return a type that will match only the exact given type.
 * The given value must be a primitive, in order to be serialized to a snapshot correctly.
 * You can use literal to match exact strings for example the exact male or female string.
 *
 * Example:
 * ```ts
 * const Person = types.model({
 *     name: types.string,
 *     gender: types.union(types.literal('male'), types.literal('female'))
 * })
 * ```
 *
 * @param value The value to use in the strict equal check
 * @returns
 */
function literal(value) {
    // check that the given value is a primitive
    assertArg(value, isPrimitive, "primitive", 1);
    return new Literal(value);
}
/**
 * Returns if a given value represents a literal type.
 *
 * @param type
 * @returns
 */
function isLiteralType(type) {
    return isType(type) && (type.flags & TypeFlags.Literal) > 0;
}

class Refinement extends BaseType {
    _subtype;
    _predicate;
    _message;
    get flags() {
        return this._subtype.flags | TypeFlags.Refinement;
    }
    constructor(name, _subtype, _predicate, _message) {
        super(name);
        this._subtype = _subtype;
        this._predicate = _predicate;
        this._message = _message;
    }
    describe() {
        return this.name;
    }
    instantiate(parent, subpath, environment, initialValue) {
        // create the child type
        return this._subtype.instantiate(parent, subpath, environment, initialValue);
    }
    isAssignableFrom(type) {
        return this._subtype.isAssignableFrom(type);
    }
    isValidSnapshot(value, context) {
        const subtypeErrors = this._subtype.validate(value, context);
        if (subtypeErrors.length > 0) {
            return subtypeErrors;
        }
        const snapshot = isStateTreeNode(value)
            ? getStateTreeNode(value).snapshot
            : value;
        if (!this._predicate(snapshot)) {
            return typeCheckFailure(context, value, this._message(value));
        }
        return typeCheckSuccess();
    }
    reconcile(current, newValue, parent, subpath) {
        return this._subtype.reconcile(current, newValue, parent, subpath);
    }
    getSubTypes() {
        return this._subtype;
    }
}
/**
 * `types.refinement` - Creates a type that is more specific than the base type, e.g. `types.refinement(types.string, value => value.length > 5)` to create a type of strings that can only be longer then 5.
 *
 * @param name
 * @param type
 * @param predicate
 * @returns
 */
function refinement(...args) {
    const name = typeof args[0] === "string"
        ? args.shift()
        : isType(args[0])
            ? args[0].name
            : null;
    const type = args[0];
    const predicate = args[1];
    const message = args[2]
        ? args[2]
        : (v) => "Value does not respect the refinement predicate";
    // ensures all parameters are correct
    assertIsType(type, [1, 2]);
    assertIsString(name, 1);
    assertIsFunction(predicate, [2, 3]);
    assertIsFunction(message, [3, 4]);
    return new Refinement(name, type, predicate, message);
}
/**
 * Returns if a given value is a refinement type.
 *
 * @param type
 * @returns
 */
function isRefinementType(type) {
    return (type.flags & TypeFlags.Refinement) > 0;
}

/**
 * `types.enumeration` - Can be used to create an string based enumeration.
 * (note: this methods is just sugar for a union of string literals)
 *
 * Example:
 * ```ts
 * const TrafficLight = types.model({
 *   color: types.enumeration("Color", ["Red", "Orange", "Green"])
 * })
 * ```
 *
 * @param name descriptive name of the enumeration (optional)
 * @param options possible values this enumeration can have
 * @returns
 */
function enumeration(name, options) {
    const realOptions = typeof name === "string" ? options : name;
    // check all options
    if (devMode()) {
        realOptions.forEach((option, i) => {
            assertIsString(option, i + 1);
        });
    }
    const type = union(...realOptions.map(option => literal("" + option)));
    if (typeof name === "string") {
        type.name = name;
    }
    return type;
}

/**
 * @internal
 * @hidden
 */
class Union extends BaseType {
    _types;
    _dispatcher;
    _eager = true;
    get flags() {
        let result = TypeFlags.Union;
        this._types.forEach(type => {
            result |= type.flags;
        });
        return result;
    }
    constructor(name, _types, options) {
        super(name);
        this._types = _types;
        options = {
            eager: true,
            dispatcher: undefined,
            ...options
        };
        this._dispatcher = options.dispatcher;
        if (!options.eager) {
            this._eager = false;
        }
    }
    isAssignableFrom(type) {
        return this._types.some(subType => subType.isAssignableFrom(type));
    }
    describe() {
        return ("(" + this._types.map(factory => factory.describe()).join(" | ") + ")");
    }
    instantiate(parent, subpath, environment, initialValue) {
        const type = this.determineType(initialValue, undefined);
        if (!type) {
            throw fail("No matching type for union " + this.describe());
        } // can happen in prod builds
        return type.instantiate(parent, subpath, environment, initialValue);
    }
    reconcile(current, newValue, parent, subpath) {
        const type = this.determineType(newValue, current.getReconciliationType());
        if (!type) {
            throw fail("No matching type for union " + this.describe());
        } // can happen in prod builds
        return type.reconcile(current, newValue, parent, subpath);
    }
    determineType(value, reconcileCurrentType) {
        // try the dispatcher, if defined
        if (this._dispatcher) {
            return this._dispatcher(value);
        }
        // fast path: when type checking is disabled, try quick structural matching first
        if (!isTypeCheckingEnabled()) {
            const quickMatch = this.tryQuickMatch(value, reconcileCurrentType);
            if (quickMatch) {
                return quickMatch;
            }
            // for plain object snapshots that didn't match via quick path, try all types
            // with quick matching before falling back to full validation
            // (state tree nodes must go through full validation for type identity checks)
            if (isPlainObject(value) && !isStateTreeNode(value)) {
                for (const type of this._types) {
                    if (this.snapshotLooksLikeType(value, type)) {
                        return type;
                    }
                }
            }
        }
        // find the most accomodating type
        // if we are using reconciliation try the current node type first (fix for #1045)
        if (reconcileCurrentType) {
            if (reconcileCurrentType.is(value)) {
                return reconcileCurrentType;
            }
            return this._types
                .filter(t => t !== reconcileCurrentType)
                .find(type => type.is(value));
        }
        else {
            return this._types.find(type => type.is(value));
        }
    }
    tryQuickMatch(value, reconcileCurrentType) {
        // state tree nodes need full type compatibility checking
        // (e.g., A.is(B.create()) must return false even if snapshots are compatible)
        if (isStateTreeNode(value)) {
            return undefined;
        }
        // for non-object values, try primitive matching
        if (!isPlainObject(value)) {
            return this.tryMatchPrimitive(value);
        }
        // for objects, try structural matching against model types
        const typesToCheck = reconcileCurrentType
            ? [
                reconcileCurrentType,
                ...this._types.filter(t => t !== reconcileCurrentType)
            ]
            : this._types;
        for (const type of typesToCheck) {
            if (this.snapshotLooksLikeType(value, type)) {
                return type;
            }
        }
        return undefined;
    }
    tryMatchPrimitive(value) {
        const valueType = typeof value;
        for (const type of this._types) {
            const flags = type.flags;
            if ((valueType === "string" && flags & TypeFlags.String) ||
                (valueType === "number" &&
                    flags &
                        (TypeFlags.Number |
                            TypeFlags.Integer |
                            TypeFlags.Float |
                            TypeFlags.Finite)) ||
                (valueType === "boolean" && flags & TypeFlags.Boolean) ||
                (value === null && flags & TypeFlags.Null) ||
                (value === undefined && flags & TypeFlags.Undefined)) {
                return type;
            }
            // for literals, check exact value match
            if (flags & TypeFlags.Literal) {
                if (type.is(value)) {
                    return type;
                }
            }
        }
        return undefined;
    }
    snapshotLooksLikeType(value, type) {
        // for model types, check if snapshot has all the required property keys
        // and that any literal-typed properties match exactly
        if (type instanceof ModelType) {
            const props = type.properties;
            // use cached propertyNames from ModelType instead of Object.keys()
            for (const key of type.propertyNames) {
                const propType = props[key];
                const isOptional = propType.flags & TypeFlags.Optional;
                const propValue = value[key];
                // check required properties exist and are not undefined
                // (unless the type accepts undefined, which Optional types do)
                if (!isOptional) {
                    if (!(key in value) || propValue === undefined) {
                        return false;
                    }
                }
                // for literal types, verify the value matches exactly
                // this is critical for discriminated unions
                if (propType.flags & TypeFlags.Literal) {
                    if (!propType.is(propValue)) {
                        return false;
                    }
                }
            }
            return true;
        }
        return false;
    }
    isValidSnapshot(value, context) {
        if (this._dispatcher) {
            return this._dispatcher(value).validate(value, context);
        }
        const allErrors = [];
        let applicableTypes = 0;
        for (let i = 0; i < this._types.length; i++) {
            const type = this._types[i];
            const errors = type.validate(value, context);
            if (errors.length === 0) {
                if (this._eager) {
                    return typeCheckSuccess();
                }
                else {
                    applicableTypes++;
                }
            }
            else {
                allErrors.push(errors);
            }
        }
        if (applicableTypes === 1) {
            return typeCheckSuccess();
        }
        return typeCheckFailure(context, value, "No type is applicable for the union").concat(flattenTypeErrors(allErrors));
    }
    getSubTypes() {
        return this._types;
    }
}
/**
 * `types.union` - Create a union of multiple types. If the correct type cannot be inferred unambiguously from a snapshot, provide a dispatcher function of the form `(snapshot) => Type`.
 *
 * @param optionsOrType
 * @param otherTypes
 * @returns
 */
function union(optionsOrType, ...otherTypes) {
    const options = isType(optionsOrType) ? undefined : optionsOrType;
    const types = isType(optionsOrType)
        ? [optionsOrType, ...otherTypes]
        : otherTypes;
    const name = "(" + types.map(type => type.name).join(" | ") + ")";
    // check all options
    if (devMode()) {
        if (options) {
            assertArg(options, o => isPlainObject(o), "object { eager?: boolean, dispatcher?: Function }", 1);
        }
        types.forEach((type, i) => {
            assertIsType(type, options ? i + 2 : i + 1);
        });
    }
    return new Union(name, types, options);
}
/**
 * Returns if a given value represents a union type.
 *
 * @param type
 * @returns
 */
function isUnionType(type) {
    return (type.flags & TypeFlags.Union) > 0;
}

/**
 * @hidden
 * @internal
 */
class OptionalValue extends BaseType {
    _subtype;
    _defaultValue;
    optionalValues;
    get flags() {
        return this._subtype.flags | TypeFlags.Optional;
    }
    constructor(_subtype, _defaultValue, optionalValues) {
        super(_subtype.name);
        this._subtype = _subtype;
        this._defaultValue = _defaultValue;
        this.optionalValues = optionalValues;
    }
    describe() {
        return this._subtype.describe() + "?";
    }
    instantiate(parent, subpath, environment, initialValue) {
        if (this.optionalValues.indexOf(initialValue) >= 0) {
            const defaultInstanceOrSnapshot = this.getDefaultInstanceOrSnapshot();
            return this._subtype.instantiate(parent, subpath, environment, defaultInstanceOrSnapshot);
        }
        return this._subtype.instantiate(parent, subpath, environment, initialValue);
    }
    reconcile(current, newValue, parent, subpath) {
        return this._subtype.reconcile(current, this.optionalValues.indexOf(newValue) < 0 && this._subtype.is(newValue)
            ? newValue
            : this.getDefaultInstanceOrSnapshot(), parent, subpath);
    }
    getDefaultInstanceOrSnapshot() {
        const defaultInstanceOrSnapshot = typeof this._defaultValue === "function"
            ? this._defaultValue()
            : this._defaultValue;
        // while static values are already snapshots and checked on types.optional
        // generator functions must always be rechecked just in case
        if (typeof this._defaultValue === "function") {
            typecheckInternal(this, defaultInstanceOrSnapshot);
        }
        return defaultInstanceOrSnapshot;
    }
    isValidSnapshot(value, context) {
        // defaulted values can be skipped
        if (this.optionalValues.indexOf(value) >= 0) {
            return typeCheckSuccess();
        }
        // bounce validation to the sub-type
        return this._subtype.validate(value, context);
    }
    isAssignableFrom(type) {
        return this._subtype.isAssignableFrom(type);
    }
    getSubTypes() {
        return this._subtype;
    }
}
function checkOptionalPreconditions(type, defaultValueOrFunction) {
    // make sure we never pass direct instances
    if (typeof defaultValueOrFunction !== "function" &&
        isStateTreeNode(defaultValueOrFunction)) {
        throw fail("default value cannot be an instance, pass a snapshot or a function that creates an instance/snapshot instead");
    }
    assertIsType(type, 1);
    if (devMode()) {
        // we only check default values if they are passed directly
        // if they are generator functions they will be checked once they are generated
        // we don't check generator function results here to avoid generating a node just for type-checking purposes
        // which might generate side-effects
        if (typeof defaultValueOrFunction !== "function") {
            typecheckInternal(type, defaultValueOrFunction);
        }
    }
}
/**
 * `types.optional` - Can be used to create a property with a default value.
 *
 * Depending on the third argument (`optionalValues`) there are two ways of operation:
 * - If the argument is not provided, then if a value is not provided in the snapshot (`undefined` or missing),
 *   it will default to the provided `defaultValue`
 * - If the argument is provided, then if the value in the snapshot matches one of the optional values inside the array then it will
 *   default to the provided `defaultValue`. Additionally, if one of the optional values inside the array is `undefined` then a missing
 *   property is also valid.
 *
 *   Note that it is also possible to include values of the same type as the intended subtype as optional values,
 *   in this case the optional value will be transformed into the `defaultValue` (e.g. `types.optional(types.string, "unnamed", [undefined, ""])`
 *   will transform the snapshot values `undefined` (and therefore missing) and empty strings into the string `"unnamed"` when it gets
 *   instantiated).
 *
 * If `defaultValue` is a function, the function will be invoked for every new instance.
 * Applying a snapshot in which the optional value is one of the optional values (or `undefined`/_not_ present if none are provided) causes the
 * value to be reset.
 *
 * Example:
 * ```ts
 * const Todo = types.model({
 *   title: types.string,
 *   subtitle1: types.optional(types.string, "", [null]),
 *   subtitle2: types.optional(types.string, "", [null, undefined]),
 *   done: types.optional(types.boolean, false),
 *   created: types.optional(types.Date, () => new Date()),
 * })
 *
 * // if done is missing / undefined it will become false
 * // if created is missing / undefined it will get a freshly generated timestamp
 * // if subtitle1 is null it will default to "", but it cannot be missing or undefined
 * // if subtitle2 is null or undefined it will default to ""; since it can be undefined it can also be missing
 * const todo = Todo.create({ title: "Get coffee", subtitle1: null })
 * ```
 *
 * @param type
 * @param defaultValueOrFunction
 * @param optionalValues an optional array with zero or more primitive values (string, number, boolean, null or undefined)
 *                       that will be converted into the default. `[ undefined ]` is assumed when none is provided
 * @returns
 */
function optional(type, defaultValueOrFunction, optionalValues) {
    checkOptionalPreconditions(type, defaultValueOrFunction);
    return new OptionalValue(type, defaultValueOrFunction, optionalValues ? optionalValues : undefinedAsOptionalValues);
}
const undefinedAsOptionalValues = [undefined];
/**
 * Returns if a value represents an optional type.
 *
 * @template IT
 * @param type
 * @returns
 */
function isOptionalType(type) {
    return isType(type) && (type.flags & TypeFlags.Optional) > 0;
}

const optionalUndefinedType = optional(undefinedType, undefined);
const optionalNullType = optional(nullType, null);
/**
 * `types.maybe` - Maybe will make a type nullable, and also optional.
 * The value `undefined` will be used to represent nullability.
 *
 * @param type
 * @returns
 */
function maybe(type) {
    assertIsType(type, 1);
    return union(type, optionalUndefinedType);
}
/**
 * `types.maybeNull` - Maybe will make a type nullable, and also optional.
 * The value `null` will be used to represent no value.
 *
 * @param type
 * @returns
 */
function maybeNull(type) {
    assertIsType(type, 1);
    return union(type, optionalNullType);
}

class Late extends BaseType {
    _definition;
    _subType;
    get flags() {
        return (this._subType ? this._subType.flags : 0) | TypeFlags.Late;
    }
    getSubType(mustSucceed) {
        if (!this._subType) {
            let t = undefined;
            try {
                t = this._definition();
            }
            catch (e) {
                if (e instanceof ReferenceError) // can happen in strict ES5 code when a definition is self refering
                 {
                    t = undefined;
                }
                else {
                    throw e;
                }
            }
            if (mustSucceed && t === undefined) {
                throw fail("Late type seems to be used too early, the definition (still) returns undefined");
            }
            if (t) {
                if (devMode() && !isType(t)) {
                    throw fail("Failed to determine subtype, make sure types.late returns a type definition.");
                }
                this._subType = t;
            }
        }
        return this._subType;
    }
    constructor(name, _definition) {
        super(name);
        this._definition = _definition;
    }
    instantiate(parent, subpath, environment, initialValue) {
        return this.getSubType(true).instantiate(parent, subpath, environment, initialValue);
    }
    reconcile(current, newValue, parent, subpath) {
        return this.getSubType(true).reconcile(current, newValue, parent, subpath);
    }
    describe() {
        const t = this.getSubType(false);
        return t ? t.name : "<uknown late type>";
    }
    isValidSnapshot(value, context) {
        const t = this.getSubType(false);
        if (!t) {
            // See #916; the variable the definition closure is pointing to wasn't defined yet, so can't be evaluted yet here
            return typeCheckSuccess();
        }
        return t.validate(value, context);
    }
    isAssignableFrom(type) {
        const t = this.getSubType(false);
        return t ? t.isAssignableFrom(type) : false;
    }
    getSubTypes() {
        const subtype = this.getSubType(false);
        return subtype ? subtype : cannotDetermineSubtype;
    }
}
/**
 * `types.late` - Defines a type that gets implemented later. This is useful when you have to deal with circular dependencies.
 * Please notice that when defining circular dependencies TypeScript isn't smart enough to inference them.
 *
 * Example:
 * ```ts
 *   // TypeScript isn't smart enough to infer self referencing types.
 *  const Node = types.model({
 *       children: types.array(types.late((): IAnyModelType => Node)) // then typecast each array element to Instance<typeof Node>
 *  })
 * ```
 *
 * @param name The name to use for the type that will be returned.
 * @param type A function that returns the type that will be defined.
 * @returns
 */
function late(nameOrType, maybeType) {
    const name = typeof nameOrType === "string"
        ? nameOrType
        : `late(${nameOrType.toString()})`;
    const type = typeof nameOrType === "string" ? maybeType : nameOrType;
    // checks that the type is actually a late type
    if (devMode()) {
        if (!(typeof type === "function" && type.length === 0)) {
            throw fail("Invalid late type, expected a function with zero arguments that returns a type, got: " +
                type);
        }
    }
    return new Late(name, type);
}
/**
 * Returns if a given value represents a late type.
 *
 * @param type
 * @returns
 */
function isLateType(type) {
    return isType(type) && (type.flags & TypeFlags.Late) > 0;
}

function lazy(name, options) {
    // TODO: fix this unknown casting to be stricter
    return new Lazy(name, options);
}
/**
 * @internal
 * @hidden
 */
class Lazy extends SimpleType {
    options;
    flags = TypeFlags.Lazy;
    loadedType = null;
    pendingNodeList = observable.array();
    constructor(name, options) {
        super(name);
        this.options = options;
        when(() => this.pendingNodeList.length > 0 &&
            this.pendingNodeList.some(node => node.isAlive &&
                this.options.shouldLoadPredicate(node.parent ? node.parent.value : null)), () => {
            this.options.loadType().then(action((type) => {
                this.loadedType = type;
                this.pendingNodeList.forEach(node => {
                    if (!node.parent) {
                        return;
                    }
                    if (!this.loadedType) {
                        return;
                    }
                    node.parent.applyPatches([
                        {
                            op: "replace",
                            path: `/${node.subpath}`,
                            value: node.snapshot
                        }
                    ]);
                });
            }));
        });
    }
    describe() {
        return `<lazy ${this.name}>`;
    }
    instantiate(parent, subpath, environment, value) {
        if (this.loadedType) {
            return this.loadedType.instantiate(parent, subpath, environment, value);
        }
        const node = createScalarNode(this, parent, subpath, environment, deepFreeze(value));
        this.pendingNodeList.push(node);
        when(() => !node.isAlive, () => this.pendingNodeList.splice(this.pendingNodeList.indexOf(node), 1));
        return node;
    }
    isValidSnapshot(value, context) {
        if (this.loadedType) {
            return this.loadedType.validate(value, context);
        }
        if (!isSerializable(value)) {
            return typeCheckFailure(context, value, "Value is not serializable and cannot be lazy");
        }
        return typeCheckSuccess();
    }
    reconcile(current, value, parent, subpath) {
        if (this.loadedType) {
            current.die();
            return this.loadedType.instantiate(parent, subpath, parent.environment, value);
        }
        return super.reconcile(current, value, parent, subpath);
    }
}

/**
 * @internal
 * @hidden
 */
class Frozen extends SimpleType {
    subType;
    flags = TypeFlags.Frozen;
    constructor(subType) {
        super(subType ? `frozen(${subType.name})` : "frozen");
        this.subType = subType;
    }
    describe() {
        return "<any immutable value>";
    }
    instantiate(parent, subpath, environment, value) {
        // create the node
        return createScalarNode(this, parent, subpath, environment, deepFreeze(value));
    }
    isValidSnapshot(value, context) {
        if (!isSerializable(value)) {
            return typeCheckFailure(context, value, "Value is not serializable and cannot be frozen");
        }
        if (this.subType) {
            return this.subType.validate(value, context);
        }
        return typeCheckSuccess();
    }
}
const untypedFrozenInstance = new Frozen();
/**
 * `types.frozen` - Frozen can be used to store any value that is serializable in itself (that is valid JSON).
 * Frozen values need to be immutable or treated as if immutable. They need be serializable as well.
 * Values stored in frozen will snapshotted as-is by MST, and internal changes will not be tracked.
 *
 * This is useful to store complex, but immutable values like vectors etc. It can form a powerful bridge to parts of your application that should be immutable, or that assume data to be immutable.
 *
 * Note: if you want to store free-form state that is mutable, or not serializeable, consider using volatile state instead.
 *
 * Frozen properties can be defined in three different ways
 * 1. `types.frozen(SubType)` - provide a valid MST type and frozen will check if the provided data conforms the snapshot for that type
 * 2. `types.frozen({ someDefaultValue: true})` - provide a primitive value, object or array, and MST will infer the type from that object, and also make it the default value for the field
 * 3. `types.frozen<TypeScriptType>()` - provide a typescript type, to help in strongly typing the field (design time only)
 *
 * Example:
 * ```ts
 * const GameCharacter = types.model({
 *   name: string,
 *   location: types.frozen({ x: 0, y: 0})
 * })
 *
 * const hero = GameCharacter.create({
 *   name: "Mario",
 *   location: { x: 7, y: 4 }
 * })
 *
 * hero.location = { x: 10, y: 2 } // OK
 * hero.location.x = 7 // Not ok!
 * ```
 *
 * ```ts
 * type Point = { x: number, y: number }
 *    const Mouse = types.model({
 *         loc: types.frozen<Point>()
 *    })
 * ```
 *
 * @param defaultValueOrType
 * @returns
 */
function frozen(arg) {
    if (arguments.length === 0) {
        return untypedFrozenInstance;
    }
    else if (isType(arg)) {
        return new Frozen(arg);
    }
    else {
        return optional(untypedFrozenInstance, arg);
    }
}
/**
 * Returns if a given value represents a frozen type.
 *
 * @param type
 * @returns
 */
function isFrozenType(type) {
    return isType(type) && (type.flags & TypeFlags.Frozen) > 0;
}

function getInvalidationCause(hook) {
    switch (hook) {
        case Hook.beforeDestroy:
            return "destroy";
        case Hook.beforeDetach:
            return "detach";
        default:
            return undefined;
    }
}
class StoredReference {
    targetType;
    identifier;
    node;
    resolvedReference;
    constructor(value, targetType) {
        this.targetType = targetType;
        if (isValidIdentifier(value)) {
            this.identifier = value;
        }
        else if (isStateTreeNode(value)) {
            const targetNode = getStateTreeNode(value);
            if (!targetNode.identifierAttribute) {
                throw fail(`Can only store references with a defined identifier attribute.`);
            }
            const id = targetNode.unnormalizedIdentifier;
            if (id === null || id === undefined) {
                throw fail(`Can only store references to tree nodes with a defined identifier.`);
            }
            this.identifier = id;
        }
        else {
            throw fail(`Can only store references to tree nodes or identifiers, got: '${value}'`);
        }
    }
    updateResolvedReference(node) {
        const normalizedId = normalizeIdentifier(this.identifier);
        const root = node.root;
        const lastCacheModification = root.identifierCache.getLastCacheModificationPerId(normalizedId);
        if (!this.resolvedReference ||
            this.resolvedReference.lastCacheModification !== lastCacheModification) {
            const { targetType } = this;
            // reference was initialized with the identifier of the target
            const target = root.identifierCache.resolve(targetType, normalizedId);
            if (!target) {
                throw new InvalidReferenceError(`[mobx-state-tree] Failed to resolve reference '${this.identifier}' to type '${this.targetType.name}' (from node: ${node.path})`);
            }
            this.resolvedReference = {
                node: target,
                lastCacheModification: lastCacheModification
            };
        }
    }
    get resolvedValue() {
        this.updateResolvedReference(this.node);
        return this.resolvedReference.node.value;
    }
}
/**
 * @internal
 * @hidden
 */
class InvalidReferenceError extends Error {
    constructor(m) {
        super(m);
        Object.setPrototypeOf(this, InvalidReferenceError.prototype);
    }
}
/**
 * @internal
 * @hidden
 */
class BaseReferenceType extends SimpleType {
    targetType;
    onInvalidated;
    flags = TypeFlags.Reference;
    constructor(targetType, onInvalidated) {
        super(`reference(${targetType.name})`);
        this.targetType = targetType;
        this.onInvalidated = onInvalidated;
    }
    describe() {
        return this.name;
    }
    isAssignableFrom(type) {
        return this.targetType.isAssignableFrom(type);
    }
    isValidSnapshot(value, context) {
        return isValidIdentifier(value)
            ? typeCheckSuccess()
            : typeCheckFailure(context, value, "Value is not a valid identifier, which is a string or a number");
    }
    fireInvalidated(cause, storedRefNode, referenceId, refTargetNode) {
        // to actually invalidate a reference we need an alive parent,
        // since it is a scalar value (immutable-ish) and we need to change it
        // from the parent
        const storedRefParentNode = storedRefNode.parent;
        if (!storedRefParentNode || !storedRefParentNode.isAlive) {
            return;
        }
        const storedRefParentValue = storedRefParentNode.storedValue;
        if (!storedRefParentValue) {
            return;
        }
        this.onInvalidated({
            cause,
            parent: storedRefParentValue,
            invalidTarget: refTargetNode ? refTargetNode.storedValue : undefined,
            invalidId: referenceId,
            replaceRef(newRef) {
                applyPatch(storedRefNode.root.storedValue, {
                    op: "replace",
                    value: newRef,
                    path: storedRefNode.path
                });
            },
            removeRef() {
                if (isModelType(storedRefParentNode.type)) {
                    this.replaceRef(undefined);
                }
                else {
                    applyPatch(storedRefNode.root.storedValue, {
                        op: "remove",
                        path: storedRefNode.path
                    });
                }
            }
        });
    }
    addTargetNodeWatcher(storedRefNode, referenceId) {
        // this will make sure the target node becomes created
        const refTargetValue = this.getValue(storedRefNode);
        if (!refTargetValue) {
            return undefined;
        }
        const refTargetNode = getStateTreeNode(refTargetValue);
        const hookHandler = (_, refTargetNodeHook) => {
            const cause = getInvalidationCause(refTargetNodeHook);
            if (!cause) {
                return;
            }
            this.fireInvalidated(cause, storedRefNode, referenceId, refTargetNode);
        };
        const refTargetDetachHookDisposer = refTargetNode.registerHook(Hook.beforeDetach, hookHandler);
        const refTargetDestroyHookDisposer = refTargetNode.registerHook(Hook.beforeDestroy, hookHandler);
        return () => {
            refTargetDetachHookDisposer();
            refTargetDestroyHookDisposer();
        };
    }
    watchTargetNodeForInvalidations(storedRefNode, identifier, customGetSet) {
        if (!this.onInvalidated) {
            return;
        }
        let onRefTargetDestroyedHookDisposer;
        // get rid of the watcher hook when the stored ref node is destroyed
        // detached is ignored since scalar nodes (where the reference resides) cannot be detached
        storedRefNode.registerHook(Hook.beforeDestroy, () => {
            if (onRefTargetDestroyedHookDisposer) {
                onRefTargetDestroyedHookDisposer();
            }
        });
        const startWatching = (sync) => {
            // re-create hook in case the stored ref gets reattached
            if (onRefTargetDestroyedHookDisposer) {
                onRefTargetDestroyedHookDisposer();
            }
            // make sure the target node is actually there and initialized
            const storedRefParentNode = storedRefNode.parent;
            const storedRefParentValue = storedRefParentNode && storedRefParentNode.storedValue;
            if (storedRefParentNode &&
                storedRefParentNode.isAlive &&
                storedRefParentValue) {
                let refTargetNodeExists;
                if (customGetSet) {
                    refTargetNodeExists = !!customGetSet.get(identifier, storedRefParentValue);
                }
                else {
                    refTargetNodeExists = storedRefNode.root.identifierCache.has(this.targetType, normalizeIdentifier(identifier));
                }
                if (!refTargetNodeExists) {
                    // we cannot change the reference in sync mode
                    // since we are in the middle of a reconciliation/instantiation and the change would be overwritten
                    // for those cases just let the wrong reference be assigned and fail upon usage
                    // (like current references do)
                    // this means that effectively this code will only run when it is created from a snapshot
                    if (!sync) {
                        this.fireInvalidated("invalidSnapshotReference", storedRefNode, identifier, null);
                    }
                }
                else {
                    onRefTargetDestroyedHookDisposer = this.addTargetNodeWatcher(storedRefNode, identifier);
                }
            }
        };
        if (storedRefNode.state === NodeLifeCycle.FINALIZED) {
            // already attached, so the whole tree is ready
            startWatching(true);
        }
        else {
            if (!storedRefNode.isRoot) {
                // start watching once the whole tree is ready
                storedRefNode.root.registerHook(Hook.afterCreationFinalization, () => {
                    // make sure to attach it so it can start listening
                    if (storedRefNode.parent) {
                        storedRefNode.parent.createObservableInstanceIfNeeded();
                    }
                });
            }
            // start watching once the node is attached somewhere / parent changes
            storedRefNode.registerHook(Hook.afterAttach, () => {
                startWatching(false);
            });
        }
    }
}
/**
 * @internal
 * @hidden
 */
class IdentifierReferenceType extends BaseReferenceType {
    constructor(targetType, onInvalidated) {
        super(targetType, onInvalidated);
    }
    getValue(storedRefNode) {
        if (!storedRefNode.isAlive) {
            return undefined;
        }
        const storedRef = storedRefNode.storedValue;
        return storedRef.resolvedValue;
    }
    getSnapshot(storedRefNode) {
        const ref = storedRefNode.storedValue;
        return ref.identifier;
    }
    instantiate(parent, subpath, environment, initialValue) {
        const identifier = isStateTreeNode(initialValue)
            ? getIdentifier(initialValue)
            : initialValue;
        const storedRef = new StoredReference(initialValue, this.targetType);
        const storedRefNode = createScalarNode(this, parent, subpath, environment, storedRef);
        storedRef.node = storedRefNode;
        this.watchTargetNodeForInvalidations(storedRefNode, identifier, undefined);
        return storedRefNode;
    }
    reconcile(current, newValue, parent, subpath) {
        if (!current.isDetaching && current.type === this) {
            const compareByValue = isStateTreeNode(newValue);
            const ref = current.storedValue;
            if ((!compareByValue && ref.identifier === newValue) ||
                (compareByValue && ref.resolvedValue === newValue)) {
                current.setParent(parent, subpath);
                return current;
            }
        }
        const newNode = this.instantiate(parent, subpath, undefined, newValue);
        current.die(); // noop if detaching
        return newNode;
    }
}
/**
 * @internal
 * @hidden
 */
class CustomReferenceType extends BaseReferenceType {
    options;
    constructor(targetType, options, onInvalidated) {
        super(targetType, onInvalidated);
        this.options = options;
    }
    getValue(storedRefNode) {
        if (!storedRefNode.isAlive) {
            return undefined;
        }
        const referencedNode = this.options.get(storedRefNode.storedValue, storedRefNode.parent ? storedRefNode.parent.storedValue : null);
        return referencedNode;
    }
    getSnapshot(storedRefNode) {
        return storedRefNode.storedValue;
    }
    instantiate(parent, subpath, environment, newValue) {
        const identifier = isStateTreeNode(newValue)
            ? this.options.set(newValue, parent ? parent.storedValue : null)
            : newValue;
        const storedRefNode = createScalarNode(this, parent, subpath, environment, identifier);
        this.watchTargetNodeForInvalidations(storedRefNode, identifier, this.options);
        return storedRefNode;
    }
    reconcile(current, newValue, parent, subpath) {
        const newIdentifier = isStateTreeNode(newValue)
            ? this.options.set(newValue, current ? current.storedValue : null)
            : newValue;
        if (!current.isDetaching &&
            current.type === this &&
            current.storedValue === newIdentifier) {
            current.setParent(parent, subpath);
            return current;
        }
        const newNode = this.instantiate(parent, subpath, undefined, newIdentifier);
        current.die(); // noop if detaching
        return newNode;
    }
}
/**
 * `types.reference` - Creates a reference to another type, which should have defined an identifier.
 * See also the [reference and identifiers](https://github.com/mobxjs/mobx-state-tree#references-and-identifiers) section.
 */
function reference(subType, options) {
    assertIsType(subType, 1);
    if (devMode()) {
        if (arguments.length === 2 && typeof arguments[1] === "string") {
            // istanbul ignore next
            throw fail("References with base path are no longer supported. Please remove the base path.");
        }
    }
    const getSetOptions = options
        ? options
        : undefined;
    const onInvalidated = options
        ? options.onInvalidated
        : undefined;
    if (getSetOptions && (getSetOptions.get || getSetOptions.set)) {
        if (devMode()) {
            if (!getSetOptions.get || !getSetOptions.set) {
                throw fail("reference options must either contain both a 'get' and a 'set' method or none of them");
            }
        }
        return new CustomReferenceType(subType, {
            get: getSetOptions.get,
            set: getSetOptions.set
        }, onInvalidated);
    }
    else {
        return new IdentifierReferenceType(subType, onInvalidated);
    }
}
/**
 * Returns if a given value represents a reference type.
 *
 * @param type
 * @returns
 */
function isReferenceType(type) {
    return (type.flags & TypeFlags.Reference) > 0;
}
/**
 * `types.safeReference` - A safe reference is like a standard reference, except that it accepts the undefined value by default
 * and automatically sets itself to undefined (when the parent is a model) / removes itself from arrays and maps
 * when the reference it is pointing to gets detached/destroyed.
 *
 * The optional options parameter object accepts a parameter named `acceptsUndefined`, which is set to true by default, so it is suitable
 * for model properties.
 * When used inside collections (arrays/maps), it is recommended to set this option to false so it can't take undefined as value,
 * which is usually the desired in those cases.
 * Additionally, the optional options parameter object accepts a parameter named `onInvalidated`, which will be called when the reference target node that the reference is pointing to is about to be detached/destroyed
 *
 * Strictly speaking it is a `types.maybe(types.reference(X))` (when `acceptsUndefined` is set to true, the default) and
 * `types.reference(X)` (when `acceptsUndefined` is set to false), both of them with a customized `onInvalidated` option.
 *
 * @param subType
 * @param options
 * @returns
 */
function safeReference(subType, options) {
    const refType = reference(subType, {
        ...options,
        onInvalidated(ev) {
            if (options && options.onInvalidated) {
                options.onInvalidated(ev);
            }
            ev.removeRef();
        }
    });
    if (options && options.acceptsUndefined === false) {
        return refType;
    }
    else {
        return maybe(refType);
    }
}

class BaseIdentifierType extends SimpleType {
    validType;
    flags = TypeFlags.Identifier;
    constructor(name, validType) {
        super(name);
        this.validType = validType;
    }
    instantiate(parent, subpath, environment, initialValue) {
        if (!parent || !(parent.type instanceof ModelType)) {
            throw fail(`Identifier types can only be instantiated as direct child of a model type`);
        }
        return createScalarNode(this, parent, subpath, environment, initialValue);
    }
    reconcile(current, newValue, parent, subpath) {
        // we don't consider detaching here since identifier are scalar nodes, and scalar nodes cannot be detached
        if (current.storedValue !== newValue) {
            throw fail(`Tried to change identifier from '${current.storedValue}' to '${newValue}'. Changing identifiers is not allowed.`);
        }
        current.setParent(parent, subpath);
        return current;
    }
    isValidSnapshot(value, context) {
        if (typeof value !== this.validType) {
            return typeCheckFailure(context, value, `Value is not a valid ${this.describe()}, expected a ${this.validType}`);
        }
        return typeCheckSuccess();
    }
}
/**
 * @internal
 * @hidden
 */
class IdentifierType extends BaseIdentifierType {
    flags = TypeFlags.Identifier;
    constructor() {
        super(`identifier`, "string");
    }
    describe() {
        return `identifier`;
    }
}
/**
 * @internal
 * @hidden
 */
class IdentifierNumberType extends BaseIdentifierType {
    constructor() {
        super("identifierNumber", "number");
    }
    getSnapshot(node) {
        return node.storedValue;
    }
    describe() {
        return `identifierNumber`;
    }
}
/**
 * `types.identifier` - Identifiers are used to make references, lifecycle events and reconciling works.
 * Inside a state tree, for each type can exist only one instance for each given identifier.
 * For example there couldn't be 2 instances of user with id 1. If you need more, consider using references.
 * Identifier can be used only as type property of a model.
 * This type accepts as parameter the value type of the identifier field that can be either string or number.
 *
 * Example:
 * ```ts
 *  const Todo = types.model("Todo", {
 *      id: types.identifier,
 *      title: types.string
 *  })
 * ```
 *
 * @returns
 */
const identifier = new IdentifierType();
/**
 * `types.identifierNumber` - Similar to `types.identifier`. This one will serialize from / to a number when applying snapshots
 *
 * Example:
 * ```ts
 *  const Todo = types.model("Todo", {
 *      id: types.identifierNumber,
 *      title: types.string
 *  })
 * ```
 *
 * @returns
 */
const identifierNumber = new IdentifierNumberType();
/**
 * Returns if a given value represents an identifier type.
 *
 * @param type
 * @returns
 */
function isIdentifierType(type) {
    return isType(type) && (type.flags & TypeFlags.Identifier) > 0;
}
/**
 * @internal
 * @hidden
 */
function normalizeIdentifier(id) {
    return "" + id;
}
/**
 * @internal
 * @hidden
 */
function isValidIdentifier(id) {
    return typeof id === "string" || typeof id === "number";
}
/**
 * @internal
 * @hidden
 */
function assertIsValidIdentifier(id, argNumber) {
    assertArg(id, isValidIdentifier, "string or number (identifier)", argNumber);
}

/**
 * `types.custom` - Creates a custom type. Custom types can be used for arbitrary immutable values, that have a serializable representation. For example, to create your own Date representation, Decimal type etc.
 *
 * The signature of the options is:
 * ```ts
 * export interface CustomTypeOptions<S, T> {
 *     // Friendly name
 *     name: string
 *     // given a serialized value and environment, how to turn it into the target type
 *     fromSnapshot(snapshot: S, env: any): T
 *     // return the serialization of the current value
 *     toSnapshot(value: T): S
 *     // if true, this is a converted value, if false, it's a snapshot
 *     isTargetType(value: T | S): value is T
 *     // a non empty string is assumed to be a validation error
 *     getValidationMessage?(snapshot: S): string
 * }
 * ```
 *
 * Example:
 * ```ts
 * const DecimalPrimitive = types.custom<string, Decimal>({
 *     name: "Decimal",
 *     fromSnapshot(value: string) {
 *         return new Decimal(value)
 *     },
 *     toSnapshot(value: Decimal) {
 *         return value.toString()
 *     },
 *     isTargetType(value: string | Decimal): boolean {
 *         return value instanceof Decimal
 *     },
 *     getValidationMessage(value: string): string {
 *         if (/^-?\d+\.\d+$/.test(value)) return "" // OK
 *         return `'${value}' doesn't look like a valid decimal number`
 *     }
 * })
 *
 * const Wallet = types.model({
 *     balance: DecimalPrimitive
 * })
 * ```
 *
 * @param options
 * @returns
 */
function custom(options) {
    return new CustomType(options);
}
/**
 * @internal
 * @hidden
 */
class CustomType extends SimpleType {
    options;
    flags = TypeFlags.Custom;
    constructor(options) {
        super(options.name);
        this.options = options;
    }
    describe() {
        return this.name;
    }
    isValidSnapshot(value, context) {
        if (this.options.isTargetType(value)) {
            return typeCheckSuccess();
        }
        const typeError = this.options.getValidationMessage(value);
        if (typeError) {
            return typeCheckFailure(context, value, `Invalid value for type '${this.name}': ${typeError}`);
        }
        return typeCheckSuccess();
    }
    getSnapshot(node) {
        return this.options.toSnapshot(node.storedValue);
    }
    instantiate(parent, subpath, environment, initialValue) {
        const valueToStore = this.options.isTargetType(initialValue)
            ? initialValue
            : this.options.fromSnapshot(initialValue, parent && parent.root.environment);
        return createScalarNode(this, parent, subpath, environment, valueToStore);
    }
    reconcile(current, value, parent, subpath) {
        const isSnapshot = !this.options.isTargetType(value);
        // in theory customs use scalar nodes which cannot be detached, but still...
        if (!current.isDetaching) {
            const unchanged = current.type === this &&
                (isSnapshot
                    ? value === current.snapshot
                    : value === current.storedValue);
            if (unchanged) {
                current.setParent(parent, subpath);
                return current;
            }
        }
        const valueToStore = isSnapshot
            ? this.options.fromSnapshot(value, parent.root.environment)
            : value;
        const newNode = this.instantiate(parent, subpath, undefined, valueToStore);
        current.die(); // noop if detaching
        return newNode;
    }
}

class Resilient extends BaseType {
    _subtype;
    _fallbackType;
    _createFallbackSnapshot;
    get flags() {
        return this._subtype.flags;
    }
    constructor(_subtype, _fallbackType, _createFallbackSnapshot) {
        super(`resilient(${_subtype.name})`);
        this._subtype = _subtype;
        this._fallbackType = _fallbackType;
        this._createFallbackSnapshot = _createFallbackSnapshot;
    }
    describe() {
        return `resilient(${this._subtype.describe()})`;
    }
    _instantiateFallback(parent, subpath, environment, originalSnapshot, originalError) {
        let fallbackSnapshot;
        try {
            fallbackSnapshot = this._createFallbackSnapshot(originalError, originalSnapshot);
        }
        catch (e) {
            throw fail(`resilient: createFallbackSnapshot threw while handling error for '${this._subtype.name}': ${originalError}. createFallbackSnapshot error: ${e}`);
        }
        try {
            return this._fallbackType.instantiate(parent, subpath, environment, fallbackSnapshot);
        }
        catch (e) {
            throw fail(`resilient: fallback type '${this._fallbackType.name}' failed to instantiate while handling error for '${this._subtype.name}': ${originalError}. Fallback error: ${e}`);
        }
    }
    instantiate(parent, subpath, environment, initialValue) {
        if (isStateTreeNode(initialValue)) {
            return this._subtype.instantiate(parent, subpath, environment, initialValue);
        }
        try {
            return this._subtype.instantiate(parent, subpath, environment, initialValue);
        }
        catch (e) {
            return this._instantiateFallback(parent, subpath, environment, initialValue, e);
        }
    }
    reconcile(current, newValue, parent, subpath) {
        if (isStateTreeNode(newValue)) {
            return this._subtype.reconcile(current, newValue, parent, subpath);
        }
        if (this._fallbackType.isAssignableFrom(current.type)) {
            try {
                return this._subtype.instantiate(parent, subpath, undefined, newValue);
            }
            catch (e) {
                return this._fallbackType.reconcile(current, this._createFallbackSnapshot(e, newValue), parent, subpath);
            }
        }
        try {
            return this._subtype.reconcile(current, newValue, parent, subpath);
        }
        catch (e) {
            current.die();
            return this._instantiateFallback(parent, subpath, undefined, newValue, e);
        }
    }
    isValidSnapshot(_value, _context) {
        return typeCheckSuccess();
    }
    is(thing) {
        if (isType(thing)) {
            return (this._subtype.isAssignableFrom(thing) ||
                this._fallbackType.isAssignableFrom(thing));
        }
        try {
            if (this._subtype.is(thing)) {
                return true;
            }
        }
        catch (_e) {
            // subtype.is() may throw (e.g. union dispatcher)
        }
        try {
            return this._fallbackType.is(thing);
        }
        catch (_e) {
            return false;
        }
    }
    isAssignableFrom(type) {
        return (this._subtype.isAssignableFrom(type) ||
            this._fallbackType.isAssignableFrom(type));
    }
    getSubTypes() {
        return [this._subtype, this._fallbackType];
    }
    getSnapshot(node) {
        return node.snapshot;
    }
}
/**
 * `types.resilient` - Wraps a type so that instantiation errors are caught
 * and a fallback type is used instead. This is useful for loading data that
 * may contain unknown or invalid subtrees (e.g., plugin types that are not
 * installed) without crashing the entire state tree.
 *
 * The `createFallbackSnapshot` callback receives the caught error and the
 * original snapshot, and must return a valid snapshot for the fallback type.
 *
 * @param type The type to wrap.
 * @param fallbackType The fallback type to use when instantiation fails.
 * @param createFallbackSnapshot Callback that produces a fallback snapshot from the error and original snapshot.
 * @returns A resilient type.
 */
function resilient(type, fallbackType, createFallbackSnapshot) {
    assertIsType(type, 1);
    assertIsType(fallbackType, 2);
    return new Resilient(type, fallbackType, createFallbackSnapshot);
}

// we import the types to re-export them inside types.
const types = {
    enumeration,
    model,
    compose,
    custom,
    reference,
    safeReference,
    union,
    optional,
    literal,
    maybe,
    maybeNull,
    refinement,
    string,
    boolean,
    number,
    integer,
    float,
    finite,
    Date: DatePrimitive,
    map,
    array,
    frozen,
    identifier,
    identifierNumber,
    late,
    lazy,
    undefined: undefinedType,
    null: nullType,
    snapshotProcessor,
    resilient
};

export { addDisposer, addMiddleware, applyAction, applyPatch, applySnapshot, cast, castFlowReturn, castToReferenceSnapshot, castToSnapshot, clone, createActionTrackingMiddleware, createActionTrackingMiddleware2, decorate, destroy, detach, escapeJsonPath, flow, getChildType, getEnv, getIdentifier, getLivelinessChecking, getMembers, getNodeId, getParent, getParentOfType, getPath, getPathParts, getPropertyMembers, getRelativePath, getRoot, getRunningActionContext, getSnapshot, getType, hasParent, hasParentOfType, isActionContextChildOf, isActionContextThisOrChildOf, isAlive, isArrayType, isFrozenType, isIdentifierType, isLateType, isLiteralType, isMapType, isModelType, isOptionalType, isPrimitiveType, isProtected, isReferenceType, isRefinementType, isRoot, isStateTreeNode, isType, isUnionType, isValidReference, joinJsonPath, onAction, onPatch, onSnapshot, process$1 as process, protect, recordActions, recordPatches, resolveIdentifier, resolvePath, setDevMode, setLivelinessChecking, setLivelynessChecking, splitJsonPath, types as t, toGenerator, toGeneratorFunction, tryReference, tryResolve, typecheck, types, unescapeJsonPath, unprotect, walk };
